/**
 * API client — fetch wrapper + tenant URL builder.
 *
 * Block 6.A / ADR-024 D6: native fetch (no axios); TanStack Query owns
 * caching and retries; this layer owns URL construction + error
 * normalization.
 *
 * Tenant slug is supplied by the caller (resolved from TenantContext at
 * call sites). Routes follow the API contract: /api/t/{slug}/v1/...
 */

import { writeFetch } from './writeFetch';
import { keepAllUnsavedWork } from './keptWork';
import { signOutInProgress } from './signOut';
import type {
  AffiliationWithOrg,
  ConnectedClientsResponse,
  IndividualDetail,
  IndividualListResponse,
  IndividualsForSearchResponse,
  MergeDraftPlan,
  MergeEventSummary,
  MergeFieldDecisions,
  MergeProposeResponse,
  ProviderDetail,
  ProviderListItem,
  ProviderListResponse,
  ProviderNote,
  ProviderNotesListResponse,
  IndividualNote,
  IndividualNotesListResponse,
  ProvidersForSearchResponse,
  ProvidersForSearchExtendedResponse,
  SearchResponse,
} from './types';

export class ApiError extends Error {
  constructor(
    public readonly status: number,
    public readonly body: unknown,
    message: string,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

/** The ApiError for a response that was not ok: the body parsed as JSON,
 *  or null when it does not parse; the message is the request line, for
 *  logs and test output, never for a screen. */
export async function apiErrorFrom(res: Response, label: string): Promise<ApiError> {
  let body: unknown = null;
  try {
    body = await res.json();
  } catch {
    body = null;
  }
  return new ApiError(res.status, body, `${label} → HTTP ${res.status}`);
}

// =====================================================================
// X.5.4 — graceful re-auth on an expired SWA session
// =====================================================================
//
// staticwebapp.config.json routes /api/t/* with allowedRoles:['authenticated']
// and responseOverrides 401 -> 302 /login -> rewrite /.auth/login/aad. With
// fetch's default redirect:'follow', a WRITE follows that same-origin 302 into
// the cross-origin AAD login chain, hits CORS, and rejects with an opaque
// TypeError the mutation swallows -> silent no-op (the 2026-06-03 "Add
// announcement does nothing" report; a hard refresh re-authed and fixed it).
//
// Fix: set redirect:'manual' on writes so the first 302 surfaces as
// res.type === 'opaqueredirect' (deterministic; our API never legitimately
// 3xx-redirects a write). On that signal we bounce the user to login with the
// current page as the return URL (AAD SSO usually completes silently) and
// throw a tagged auth_expired ApiError so the caller surfaces it rather than
// swallowing it.

// A lapsed session on a READ looks different: the default fetch follows
// the same-origin 302 into the cross-origin sign-in page, which rejects
// with a TypeError, the same shape as a network outage. The query client
// asks the platform (redirectIfSignedOut) which of the two it was.
let signedOutCheck: Promise<boolean> | null = null;

export const authExpiry = {
  /** A sign-out already under way (src/lib/signOut.ts) is taking the
   *  page to the sign-in screen; a read failing in that same moment
   *  must not send it to Microsoft instead. */
  //
  // Before the page leaves, every page's unsaved work is kept
  // (src/lib/keptWork.ts), so a person who has typed for an hour signs
  // back in to it. `route` is the API route of a write that met the
  // expired sign-in; absent for a read, which has nothing in flight.
  redirectToLogin(route?: string): void {
    if (typeof window === 'undefined') return;
    if (signOutInProgress()) return;
    keepAllUnsavedWork(route ?? null);
    const ret = window.location.pathname + window.location.search + window.location.hash;
    authExpiry.go(`/.auth/login/aad?post_login_redirect_uri=${encodeURIComponent(ret)}`);
  },

  /** The step to sign-in itself. Its own member so a test can stop the
   *  page from leaving. */
  go(url: string): void {
    window.location.assign(url);
  },

  /**
   * Ask the platform whether anyone is still signed in; if not, go to
   * sign-in and resolve true. A check already in flight is shared, so a
   * page whose every read failed at once asks exactly once. Any failure
   * of the check itself resolves false: a real outage stays an error.
   */
  redirectIfSignedOut(route?: string): Promise<boolean> {
    if (signOutInProgress()) return Promise.resolve(true);
    if (signedOutCheck) return signedOutCheck;
    signedOutCheck = (async () => {
      try {
        const r = await fetch('/.auth/me', { cache: 'no-store' });
        if (!r.ok) return false;
        const data = (await r.json()) as { clientPrincipal: unknown };
        if (data.clientPrincipal === null) {
          authExpiry.redirectToLogin(route);
          return true;
        }
        return false;
      } catch {
        return false;
      } finally {
        signedOutCheck = null;
      }
    })();
    return signedOutCheck;
  },
};

/**
 * The tagged "SWA session expired" ApiError. `workKept` says whether the
 * page's work was kept and the page is going to sign-in (a kept surface,
 * src/lib/keptWork.ts): the sentence a screen shows depends on it, since
 * "your work is kept" must be true where it shows.
 */
export function authExpiredError(label: string, workKept: boolean): ApiError {
  return new ApiError(401, { error: 'auth_expired', work_kept: workKept }, `${label} -> session expired (re-auth)`);
}

/** Whether an expired sign-in kept the page's work (authExpiredError). */
export function authExpiredWorkKept(err: unknown): boolean {
  return (
    err instanceof ApiError &&
    typeof err.body === 'object' &&
    err.body !== null &&
    (err.body as { work_kept?: unknown }).work_kept === true
  );
}

/** True when an error is the tagged "SWA session expired" ApiError. */
export function isAuthExpiredError(err: unknown): boolean {
  return (
    err instanceof ApiError &&
    err.status === 401 &&
    typeof err.body === 'object' &&
    err.body !== null &&
    (err.body as { error?: string }).error === 'auth_expired'
  );
}

/** True when the server answered that the database is busy: every
 *  connection was in use. Retried once, later (queryClient.ts). */
export function isDatabaseBusy(err: unknown): boolean {
  return (
    err instanceof ApiError &&
    err.status === 503 &&
    typeof err.body === 'object' &&
    err.body !== null &&
    (err.body as { error?: string }).error === 'database_busy'
  );
}

/**
 * Detect the SWA auth redirect on a write response (redirect:'manual' turns
 * the 302 into an opaqueredirect). Triggers a clean re-login and throws the
 * tagged error so callers report "session expired" instead of a silent no-op.
 */
function throwIfAuthRedirect(res: Response, label: string): void {
  if (res.type === 'opaqueredirect') {
    authExpiry.redirectToLogin(label);
    throw authExpiredError(label, true);
  }
}

function tenantUrl(slug: string, path: string): string {
  return `/api/t/${encodeURIComponent(slug)}/v1${path}`;
}

async function get<T>(slug: string, path: string, signal?: AbortSignal): Promise<T> {
  const url = tenantUrl(slug, path);
  const res = await fetch(url, {
    method: 'GET',
    headers: { Accept: 'application/json' },
    redirect: 'manual',
    signal,
  });
  throwIfAuthRedirect(res, `GET ${path}`);
  if (!res.ok) {
    let body: unknown;
    try {
      body = await res.json();
    } catch {
      body = await res.text().catch(() => null);
    }
    throw new ApiError(res.status, body, `GET ${path} → HTTP ${res.status}`);
  }
  return (await res.json()) as T;
}

async function patch<T>(
  slug: string,
  path: string,
  body: Record<string, unknown>,
  signal?: AbortSignal,
): Promise<T> {
  const url = tenantUrl(slug, path);
  const res = await writeFetch(url, {
    method: 'PATCH',
    headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
    redirect: 'manual',
    signal,
  }, { redirectToSignIn: true });
  if (!res.ok) {
    let parsedBody: unknown;
    try {
      parsedBody = await res.json();
    } catch {
      parsedBody = await res.text().catch(() => null);
    }
    throw new ApiError(res.status, parsedBody, `PATCH ${path} → HTTP ${res.status}`);
  }
  return (await res.json()) as T;
}

async function put<T>(
  slug: string,
  path: string,
  body: Record<string, unknown>,
  signal?: AbortSignal,
): Promise<T> {
  const url = tenantUrl(slug, path);
  const res = await writeFetch(url, {
    method: 'PUT',
    headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
    redirect: 'manual',
    signal,
  }, { redirectToSignIn: true });
  if (!res.ok) {
    let parsedBody: unknown;
    try {
      parsedBody = await res.json();
    } catch {
      parsedBody = await res.text().catch(() => null);
    }
    throw new ApiError(res.status, parsedBody, `PUT ${path} → HTTP ${res.status}`);
  }
  return (await res.json()) as T;
}

async function post<T>(
  slug: string,
  path: string,
  body: Record<string, unknown> | undefined,
  signal?: AbortSignal,
): Promise<T> {
  const url = tenantUrl(slug, path);
  const res = await writeFetch(url, {
    method: 'POST',
    headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
    body: body !== undefined ? JSON.stringify(body) : undefined,
    redirect: 'manual',
    signal,
  }, { redirectToSignIn: true });
  if (!res.ok) {
    let parsedBody: unknown;
    try {
      parsedBody = await res.json();
    } catch {
      parsedBody = await res.text().catch(() => null);
    }
    throw new ApiError(res.status, parsedBody, `POST ${path} → HTTP ${res.status}`);
  }
  return (await res.json()) as T;
}

/**
 * DELETE helper. The server returns 204 No Content on success (no body),
 * so this resolves to void and does NOT attempt to parse JSON on success.
 * Error bodies are parsed defensively like the other verbs.
 */
async function del(slug: string, path: string, signal?: AbortSignal): Promise<void> {
  const url = tenantUrl(slug, path);
  const res = await writeFetch(url, {
    method: 'DELETE',
    headers: { Accept: 'application/json' },
    redirect: 'manual',
    signal,
  }, { redirectToSignIn: true });
  if (!res.ok) {
    let parsedBody: unknown;
    try {
      parsedBody = await res.json();
    } catch {
      parsedBody = await res.text().catch(() => null);
    }
    throw new ApiError(res.status, parsedBody, `DELETE ${path} → HTTP ${res.status}`);
  }
}

/**
 * DELETE that returns a JSON body (200), not 204. Used by endpoints whose
 * delete path reports facts (e.g. the checklist-template item delete returns
 * { dry_run, impact, item }). Mirrors `del` but parses + returns the body.
 */
async function delJson<T>(slug: string, path: string, signal?: AbortSignal): Promise<T> {
  const url = tenantUrl(slug, path);
  const res = await writeFetch(url, {
    method: 'DELETE',
    headers: { Accept: 'application/json' },
    redirect: 'manual',
    signal,
  }, { redirectToSignIn: true });
  if (!res.ok) {
    let parsedBody: unknown;
    try {
      parsedBody = await res.json();
    } catch {
      parsedBody = await res.text().catch(() => null);
    }
    throw new ApiError(res.status, parsedBody, `DELETE ${path} → HTTP ${res.status}`);
  }
  return (await res.json()) as T;
}

/**
 * POST to an NDJSON streaming generate endpoint (Phase X.3.5.1).
 *
 * The server keeps the connection alive past the SWA edge idle cap by
 * emitting {"type":"heartbeat"} lines during generation, then a terminal
 * {"type":"result","data":{...}} or {"type":"error","error":"<code>"} line.
 * The SWA edge buffers delivery, so res.text() returns the whole stream at
 * once; we split lines, ignore heartbeats, and act on the terminal line.
 *
 * Error mapping keeps callers' existing handling intact:
 *   - Pre-stream failures (auth 403 / rate-limit 429 / validation 400) come
 *     back as a normal non-2xx JSON body → ApiError(status, body) as before.
 *   - In-band terminal errors (post-200) → ApiError(502, { error: code }),
 *     matching the pre-X.3.5.1 502 shape the forms already branch on.
 */
async function postNdjson<T>(
  slug: string,
  path: string,
  body: Record<string, unknown>,
  signal?: AbortSignal,
): Promise<T> {
  const url = tenantUrl(slug, path);
  const res = await writeFetch(url, {
    method: 'POST',
    headers: {
      Accept: 'application/x-ndjson, application/json',
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(body),
    signal,
  }, { redirectToSignIn: true });
  if (!res.ok) {
    let parsedBody: unknown;
    try {
      parsedBody = await res.json();
    } catch {
      parsedBody = await res.text().catch(() => null);
    }
    throw new ApiError(res.status, parsedBody, `POST ${path} → HTTP ${res.status}`);
  }
  const text = await res.text();
  let terminal: { type?: string; data?: unknown; error?: string } | null = null;
  for (const line of text.split('\n')) {
    const trimmed = line.trim();
    if (trimmed.length === 0) continue;
    let obj: { type?: string; data?: unknown; error?: string };
    try {
      obj = JSON.parse(trimmed) as typeof obj;
    } catch {
      continue;
    }
    if (obj.type === 'heartbeat') continue;
    if (obj.type === 'result' || obj.type === 'error') terminal = obj;
  }
  if (!terminal) {
    // Stream closed without a terminal line (e.g. connection cut). Surface
    // as a transport failure so the caller's error handling fires.
    throw new ApiError(502, { error: 'stream_incomplete' }, `POST ${path} → incomplete NDJSON`);
  }
  if (terminal.type === 'error') {
    throw new ApiError(
      502,
      { error: terminal.error ?? 'anthropic_call_failed' },
      `POST ${path} → ${terminal.error ?? 'error'}`,
    );
  }
  return terminal.data as T;
}

export const api = {
  /**
   * Build URLSearchParams from a record, dropping undefined / null /
   * empty-string values. Centralizes the param-construction discipline
   * so callers don't accidentally send `?kind=` with empty value.
   */
  buildQuery(params: Record<string, string | number | boolean | undefined | null>): string {
    const sp = new URLSearchParams();
    for (const [k, v] of Object.entries(params)) {
      if (v === undefined || v === null || v === '') continue;
      sp.set(k, String(v));
    }
    const s = sp.toString();
    return s ? `?${s}` : '';
  },

  listProviders(
    slug: string,
    params: { active?: boolean; kind?: string; state?: string; limit?: number; offset?: number },
    signal?: AbortSignal,
  ): Promise<ProviderListResponse> {
    return get<ProviderListResponse>(slug, `/providers${api.buildQuery(params)}`, signal);
  },

  getProvider(slug: string, id: string, signal?: AbortSignal): Promise<ProviderDetail> {
    return get<ProviderDetail>(slug, `/providers/${encodeURIComponent(id)}`, signal);
  },

  // Block 6.F.3 — full CM Notes history for the detail-surface CmNotes
  // section. The HTTP handler already exists at providers.ts:454-475
  // (predates 6.F.3); 6.F.3 added note_type + author_display_name to the
  // wire shape via repo updates (no handler change needed).
  listProviderNotes(
    slug: string,
    providerId: string,
    signal?: AbortSignal,
  ): Promise<ProviderNotesListResponse> {
    return get<ProviderNotesListResponse>(
      slug,
      `/providers/${encodeURIComponent(providerId)}/notes`,
      signal,
    );
  },

  // Block 6.F.4 — write a versioned note. POST /providers/{id}/notes
  // handler at providers.ts:390-446 (extended in 6.F.4 to accept
  // note_type from body). Returns the discriminated ProviderNote shape
  // (anonymous variant omits author_user_id + author_display_name).
  createProviderNote(
    slug: string,
    providerId: string,
    body: {
      body: string;
      is_anonymous?: boolean;
      note_type?: 'tip' | 'warning' | 'general';
    },
    signal?: AbortSignal,
  ): Promise<ProviderNote> {
    return post<ProviderNote>(
      slug,
      `/providers/${encodeURIComponent(providerId)}/notes`,
      body,
      signal,
    );
  },

  /** The Directory search; `type` narrows it to one kind of entry. */
  searchProviders(
    slug: string,
    params: { q: string; limit?: number; offset?: number; type?: 'organization' | 'individual' },
    signal?: AbortSignal,
  ): Promise<SearchResponse> {
    return get<SearchResponse>(slug, `/providers/search${api.buildQuery(params)}`, signal);
  },

  // Block 6.F.2 bulk-load endpoints — consumed by useUnifiedSearch
  // (client-side fuse.js search). Cached via TanStack Query
  // staleTime=5min to mirror peace-hub's session-storage CACHE_TTL.
  fetchAllProvidersForSearch(
    slug: string,
    signal?: AbortSignal,
  ): Promise<ProvidersForSearchResponse> {
    // Phase 1.8 — endpoint now returns ProvidersForSearchExtendedResponse
    // (adds service_area_csas/msas/metro_divisions/zips alongside rows).
    // Backward-compat: useUnifiedSearch consumes the full extended shape;
    // existing callers that only need `rows` field continue to work
    // (extra fields are ignored).
    return get<ProvidersForSearchResponse>(slug, `/providers/all`, signal);
  },

  /**
   * Phase 1.8 — typed accessor for the extended bulk-load response.
   * Returns the same payload as fetchAllProvidersForSearch but typed
   * with the full ProvidersForSearchExtendedResponse so consumers can
   * read the tier-table arrays.
   */
  fetchAllProvidersForSearchExtended(
    slug: string,
    signal?: AbortSignal,
  ): Promise<ProvidersForSearchExtendedResponse> {
    return get<ProvidersForSearchExtendedResponse>(slug, `/providers/all`, signal);
  },

  fetchAllIndividualsForSearch(
    slug: string,
    signal?: AbortSignal,
  ): Promise<IndividualsForSearchResponse> {
    return get<IndividualsForSearchResponse>(slug, `/individuals/all`, signal);
  },

  listIndividuals(
    slug: string,
    params: { active?: boolean; limit?: number; offset?: number },
    signal?: AbortSignal,
  ): Promise<IndividualListResponse> {
    return get<IndividualListResponse>(slug, `/individuals${api.buildQuery(params)}`, signal);
  },

  getIndividual(slug: string, id: string, signal?: AbortSignal): Promise<IndividualDetail> {
    return get<IndividualDetail>(slug, `/individuals/${encodeURIComponent(id)}`, signal);
  },

  // Directory care-web: the clients whose active care-team rows link this
  // directory individual. Server gate: the roster's width
  // (readsEveryClient: view_all_clinical, or an internal lane holder under
  // the tenant's Everyone on staff setting); the FE affordance mirrors it
  // (ConnectedClients renders only for canReadEveryClient).
  getConnectedClients(
    slug: string,
    id: string,
    signal?: AbortSignal,
  ): Promise<ConnectedClientsResponse> {
    return get<ConnectedClientsResponse>(
      slug,
      `/individuals/${encodeURIComponent(id)}/connected-clients`,
      signal,
    );
  },

  // Directory care-web — the ORG arm; mirrors getConnectedClients (the
  // individual method) for a directory organization's connected clients.
  getConnectedClientsForOrg(
    slug: string,
    id: string,
    signal?: AbortSignal,
  ): Promise<ConnectedClientsResponse> {
    return get<ConnectedClientsResponse>(
      slug,
      `/providers/${encodeURIComponent(id)}/connected-clients`,
      signal,
    );
  },

  // ---- mutations (Block 6.B.1) ----

  updateProvider(
    slug: string,
    id: string,
    patch_: Record<string, unknown>,
    signal?: AbortSignal,
  ): Promise<ProviderDetail> {
    return patch<ProviderDetail>(slug, `/providers/${encodeURIComponent(id)}`, patch_, signal);
  },

  // Phase 1.10 — assignment-mutation endpoints for the 4 A5 fields
  // dropped from PATCHABLE_FIELDS at migration 0069. Single-value
  // highlight families (medicaid / memory_care / gender_restrictions)
  // set or clear via setProviderHighlight; multi-value service models
  // replace via setProviderServiceModels. PUT-replace semantic with
  // POST (api.ts has no `put` helper; existing pattern uses POST for
  // non-CRUD-pure replace actions per /individuals/{id}/verify).
  setProviderHighlight(
    slug: string,
    providerId: string,
    family: 'medicaid' | 'memory_care' | 'gender_restrictions',
    displayName: string | null,
    signal?: AbortSignal,
  ): Promise<{ display_name: string | null }> {
    return post<{ display_name: string | null }>(
      slug,
      `/providers/${encodeURIComponent(providerId)}/highlight-assignments/${encodeURIComponent(family)}`,
      { display_name: displayName },
      signal,
    );
  },
  setProviderServiceModels(
    slug: string,
    providerId: string,
    displayNames: string[],
    signal?: AbortSignal,
  ): Promise<{ display_names: string[] }> {
    return post<{ display_names: string[] }>(
      slug,
      `/providers/${encodeURIComponent(providerId)}/service-model-assignments`,
      { display_names: displayNames },
      signal,
    );
  },

  // Phase 1.13.2 — toggle the tenant's "Recommended" curation flag.
  // Server gates on `recommend_provider` capability (owner +
  // data_steward only). POST + literal-suffix route per the
  // setPrimaryAffiliation / setProviderHighlight convention.
  setProviderRecommendation(
    slug: string,
    providerId: string,
    isRecommended: boolean,
    signal?: AbortSignal,
  ): Promise<ProviderListItem> {
    return post<ProviderListItem>(
      slug,
      `/providers/${encodeURIComponent(providerId)}/recommendation`,
      { is_recommended: isRecommended },
      signal,
    );
  },

  updateIndividual(
    slug: string,
    id: string,
    patch_: Record<string, unknown>,
    signal?: AbortSignal,
  ): Promise<IndividualDetail> {
    return patch<IndividualDetail>(slug, `/individuals/${encodeURIComponent(id)}`, patch_, signal);
  },

  // Recommended people (0197): the same toggle as setProviderRecommendation,
  // on a person. Server gates on `recommend_provider`.
  setIndividualRecommendation(
    slug: string,
    id: string,
    isRecommended: boolean,
    signal?: AbortSignal,
  ): Promise<IndividualDetail> {
    return post<IndividualDetail>(
      slug,
      `/individuals/${encodeURIComponent(id)}/recommendation`,
      { is_recommended: isRecommended },
      signal,
    );
  },

  verifyIndividual(
    slug: string,
    id: string,
    signal?: AbortSignal,
  ): Promise<IndividualDetail> {
    return post<IndividualDetail>(
      slug,
      `/individuals/${encodeURIComponent(id)}/verify`,
      undefined,
      signal,
    );
  },

  // ---- Affiliation mutations (Block 6.B.3) ----

  createAffiliation(
    slug: string,
    body: {
      provider_individual_id: string;
      provider_id: string;
      role?: string | null;
      title?: string | null;
      is_primary?: boolean;
      start_date?: string | null;
      end_date?: string | null;
    },
    signal?: AbortSignal,
  ): Promise<AffiliationWithOrg> {
    return post<AffiliationWithOrg>(slug, '/affiliations', body as Record<string, unknown>, signal);
  },

  updateAffiliation(
    slug: string,
    individualId: string,
    providerId: string,
    body: {
      role?: string | null;
      title?: string | null;
      start_date?: string | null;
      end_date?: string | null;
    },
    signal?: AbortSignal,
  ): Promise<AffiliationWithOrg> {
    return patch<AffiliationWithOrg>(
      slug,
      `/affiliations/${encodeURIComponent(individualId)}/${encodeURIComponent(providerId)}`,
      body as Record<string, unknown>,
      signal,
    );
  },

  setPrimaryAffiliation(
    slug: string,
    individualId: string,
    providerId: string,
    signal?: AbortSignal,
  ): Promise<AffiliationWithOrg> {
    return post<AffiliationWithOrg>(
      slug,
      `/affiliations/${encodeURIComponent(individualId)}/${encodeURIComponent(providerId)}/set-primary`,
      undefined,
      signal,
    );
  },

  clearPrimaryAffiliation(
    slug: string,
    individualId: string,
    providerId: string,
    signal?: AbortSignal,
  ): Promise<AffiliationWithOrg> {
    return post<AffiliationWithOrg>(
      slug,
      `/affiliations/${encodeURIComponent(individualId)}/${encodeURIComponent(providerId)}/clear-primary`,
      undefined,
      signal,
    );
  },

  deleteAffiliation(
    slug: string,
    individualId: string,
    providerId: string,
    signal?: AbortSignal,
  ): Promise<{ deleted: boolean }> {
    const url = tenantUrl(
      slug,
      `/affiliations/${encodeURIComponent(individualId)}/${encodeURIComponent(providerId)}`,
    );
    return writeFetch(url, {
      method: 'DELETE',
      headers: { Accept: 'application/json' },
      signal,
    }, { redirectToSignIn: true }).then(async (res) => {
      if (!res.ok) {
        let parsedBody: unknown;
        try {
          parsedBody = await res.json();
        } catch {
          parsedBody = null;
        }
        throw new ApiError(res.status, parsedBody, `DELETE ${url} → HTTP ${res.status}`);
      }
      return (await res.json()) as { deleted: boolean };
    });
  },

  // Phase 1.14.2 — UPDATE-in-place edit of a provider note.
  // Server gates on `add_notes` capability (same gate as add — edit
  // is the author-side affordance, same 5 roles). Migration 0074
  // enabled UPDATE on provider_notes; the handler whitelists which
  // columns can be modified (body/note_type/is_anonymous).
  // Structural columns (id, version, author_user_id, created_at,
  // provider_id, tenant_id) are immutable from this path —
  // specifically author_user_id stays with the original creator;
  // the editor's identity lives in change_log.actor_user_id.
  updateProviderNote(
    slug: string,
    providerId: string,
    noteId: string,
    patch: {
      body?: string;
      is_anonymous?: boolean;
      note_type?: 'tip' | 'warning' | 'general';
    },
    signal?: AbortSignal,
  ): Promise<ProviderNote> {
    const url = tenantUrl(
      slug,
      `/providers/${encodeURIComponent(providerId)}/notes/${encodeURIComponent(noteId)}`,
    );
    return writeFetch(url, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(patch),
      signal,
    }, { redirectToSignIn: true }).then(async (res) => {
      if (!res.ok) {
        let parsedBody: unknown;
        try {
          parsedBody = await res.json();
        } catch {
          parsedBody = null;
        }
        throw new ApiError(res.status, parsedBody, `PATCH ${url} → HTTP ${res.status}`);
      }
      return (await res.json()) as ProviderNote;
    });
  },

  // Phase 1.14.1 — hard-delete a single provider note.
  // The server lets a holder of `delete_provider_note` delete any note and
  // an `add_notes` holder delete her own (403 otherwise). Returns 204 No
  // Content on success. Server-side
  // change_log emission captures metadata only (no body).
  deleteProviderNote(
    slug: string,
    providerId: string,
    noteId: string,
    signal?: AbortSignal,
  ): Promise<void> {
    const url = tenantUrl(
      slug,
      `/providers/${encodeURIComponent(providerId)}/notes/${encodeURIComponent(noteId)}`,
    );
    return writeFetch(url, {
      method: 'DELETE',
      headers: { Accept: 'application/json' },
      signal,
    }, { redirectToSignIn: true }).then(async (res) => {
      if (!res.ok) {
        let parsedBody: unknown;
        try {
          parsedBody = await res.json();
        } catch {
          parsedBody = null;
        }
        throw new ApiError(res.status, parsedBody, `DELETE ${url} → HTTP ${res.status}`);
      }
      // 204 No Content — no body to parse.
    });
  },

  // ---- Team notes on a Directory person (individual_notes, 0242) ------
  // The organization note calls' twins, under /individuals/{id}/notes,
  // with the same gates: add and edit on add_notes, delete by the note's
  // author or a delete_provider_note holder, the list open to every
  // signed-in seat.

  listIndividualNotes(slug: string, individualId: string, signal?: AbortSignal): Promise<IndividualNotesListResponse> {
    return get<IndividualNotesListResponse>(slug, `/individuals/${encodeURIComponent(individualId)}/notes`, signal);
  },

  createIndividualNote(
    slug: string,
    individualId: string,
    body: { body: string; is_anonymous?: boolean; note_type?: 'tip' | 'warning' | 'general' },
    signal?: AbortSignal,
  ): Promise<IndividualNote> {
    return post<IndividualNote>(slug, `/individuals/${encodeURIComponent(individualId)}/notes`, body, signal);
  },

  updateIndividualNote(
    slug: string,
    individualId: string,
    noteId: string,
    patchBody: { body?: string; is_anonymous?: boolean; note_type?: 'tip' | 'warning' | 'general' },
    signal?: AbortSignal,
  ): Promise<IndividualNote> {
    return patch<IndividualNote>(
      slug,
      `/individuals/${encodeURIComponent(individualId)}/notes/${encodeURIComponent(noteId)}`,
      patchBody,
      signal,
    );
  },

  deleteIndividualNote(slug: string, individualId: string, noteId: string, signal?: AbortSignal): Promise<void> {
    return del(slug, `/individuals/${encodeURIComponent(individualId)}/notes/${encodeURIComponent(noteId)}`, signal);
  },

  // ---- Phase 1.14.3: merge listings ----------------------------------

  // POST /providers/{loserId}/merge/propose-into/{winnerId}
  // Body: { field_decisions } — operator's per-field picks. Returns the
  // saved draft id + the computed plan (reparent counts + conflict surface).
  proposeProviderMerge(
    slug: string,
    loserId: string,
    winnerId: string,
    fieldDecisions: MergeFieldDecisions,
    signal?: AbortSignal,
  ): Promise<MergeProposeResponse> {
    return post<MergeProposeResponse>(
      slug,
      `/providers/${encodeURIComponent(loserId)}/merge/propose-into/${encodeURIComponent(winnerId)}`,
      { field_decisions: fieldDecisions },
      signal,
    );
  },

  updateProviderMergeDraft(
    slug: string,
    eventId: string,
    fieldDecisions: MergeFieldDecisions,
    signal?: AbortSignal,
  ): Promise<{ merge_event_id: string }> {
    return patch<{ merge_event_id: string }>(
      slug,
      `/providers/merges/${encodeURIComponent(eventId)}`,
      { field_decisions: fieldDecisions },
      signal,
    );
  },

  commitProviderMerge(
    slug: string,
    eventId: string,
    signal?: AbortSignal,
  ): Promise<{ winner_id: string; committed_at: string }> {
    return post<{ winner_id: string; committed_at: string }>(
      slug,
      `/providers/merges/${encodeURIComponent(eventId)}/commit`,
      undefined,
      signal,
    );
  },

  cancelProviderMergeDraft(
    slug: string,
    eventId: string,
    reason: string,
    signal?: AbortSignal,
  ): Promise<{ merge_event_id: string }> {
    return post<{ merge_event_id: string }>(
      slug,
      `/providers/merges/${encodeURIComponent(eventId)}/cancel`,
      { reason },
      signal,
    );
  },

  listProviderMerges(
    slug: string,
    signal?: AbortSignal,
  ): Promise<{ merges: MergeEventSummary[] }> {
    return get<{ merges: MergeEventSummary[] }>(
      slug,
      `/providers/merges?status=draft`,
      signal,
    );
  },

  getProviderMerge(
    slug: string,
    eventId: string,
    signal?: AbortSignal,
  ): Promise<MergeEventSummary> {
    return get<MergeEventSummary>(
      slug,
      `/providers/merges/${encodeURIComponent(eventId)}`,
      signal,
    );
  },
};

// Re-export for UI components that import from api directly.
export type {
  MergeDraftPlan,
  MergeEventSummary,
  MergeFieldDecisions,
  MergeProposeResponse,
};

// =====================================================================
// Phase X.3.4 / X.3.4.5 — AI Tools API (Email Helper + drafts inbox)
// =====================================================================

/**
 * X.3.4.5: form-friction reduction. Dropped fields:
 *   - recipient_role (server infers from recipient placeholder hint)
 *   - tone           (server defaults to 'warm-update')
 *
 * X.3.4.6: further reduction. Dropped field:
 *   - constraints    (per-call constraints were thought to surface too
 *                     rarely to keep in the operator-facing form)
 *
 * X.3.4.8: `constraints` RESTORED (optional, per-call "special
 *   instructions"). Operator user-feedback 2026-05-29 showed per-call
 *   format/structure constraints surface more often than the X.3.4.6
 *   removal assumed. Standing per-CM / per-tenant constraints remain an
 *   X.3.7 admin-panel concern; this is only the per-call surface.
 */
export interface EmailDraftInput {
  recipient?: string;
  topic?: string;
  constraints?: string; // X.3.4.8 restoration; optional per the form
  // X.3.4.10: 'spice' swaps in the SPICE post-visit-update voice and drops
  // billing. Absent → 'care_management' (server default).
  mode?: 'care_management' | 'spice';
  // 'polish' proofreads `text` (the writer's own email) on the chosen face
  // and reads no other field. Absent → the drafting pass.
  task?: 'draft' | 'polish';
  text?: string;
  // B2: Care-Management tone register. Absent → 'standard' (server default).
  // Only meaningful for Care Management; SPICE ignores it.
  tone?: 'warmer' | 'standard' | 'formal';
}

export type EmailAnnotationType = 'verify' | 'unclear' | 'notes' | 'notice';

export interface EmailAnnotation {
  type: EmailAnnotationType;
  text: string;
  /**
   * The exact words in the email body the note is about, or null when the
   * note is about the dictation or something left out. Absent on drafts
   * saved before notes carried anchors, which read as null. The note shows
   * only while its words are in the body (src/lib/annotationAnchors.ts).
   */
  anchor?: string | null;
}

export interface EmailBillingSuggestion {
  hours: number;
  category: string;
  rationale: string;
}

/**
 * X.3.4.5 structured response shape. ai_invocation_id is echoed so the
 * Save Draft button can POST it as the FK when persisting to ai_drafts.
 */
export interface EmailDraftResponse {
  ai_invocation_id: number;
  email_body: string;
  annotations: EmailAnnotation[];
  // X.3.4.10: absent for SPICE drafts (SPICE is paid by the session, so it
  // carries no billing). Present for Care Management drafts. Consumers must
  // guard on presence before rendering the billing block.
  billing_suggestion?: EmailBillingSuggestion;
  // Polish only: one short line per correction ("'recieve' to 'receive'"),
  // and the recipient read off the greeting ('' when there is none).
  changes?: string[];
  recipient?: string;
  // SPICE face only: one plain sentence naming the medical or care content
  // the email carries, shown above the draft; null when none.
  medical_flag?: string | null;
  request_id: string;
  usage: {
    input_tokens: number;
    output_tokens: number;
    cache_creation_input_tokens: number;
    cache_read_input_tokens: number;
  };
}

/**
 * Second-round edits: refine (make changes and run again). Sends the
 * CURRENT draft body (including the operator's hand edits) + the original
 * form context + the change request; the server asks the model to REVISE,
 * not regenerate. The response is the same EmailDraftResponse shape with a
 * NEW ai_invocation_id, so the existing save paths work unchanged.
 */
export interface EmailRefineInput {
  current_body: string;
  instruction: string;
  recipient: string;
  topic: string;
  constraints?: string;
  mode?: 'care_management' | 'spice';
  tone?: 'warmer' | 'standard' | 'formal';
}

export interface SaveEmailDraftInput {
  ai_invocation_id: number;
  email_body: string;
  annotations: EmailAnnotation[];
  // X.3.4.10: absent for SPICE drafts (no billing). Present for Care
  // Management drafts.
  billing_suggestion?: EmailBillingSuggestion;
  // X.3.4.10: persisted on the draft's input_payload so the detail view
  // knows to hide billing for SPICE drafts. Absent → 'care_management'.
  mode?: 'care_management' | 'spice';
  // SPICE only: the medical or care content flag, kept with the draft.
  medical_flag?: string | null;
  // X.3.4.6 dropped `constraints` from form_input; X.3.4.8 restored it
  // (optional). Old saved drafts without it continue to load fine (the
  // save handler already tolerated the field per its X.3.4.5 shape).
  form_input: {
    recipient: string;
    topic: string;
    constraints?: string; // X.3.4.8 restoration
    tone?: 'warmer' | 'standard' | 'formal'; // B2: persisted tone register
  };
}

export interface SaveEmailDraftResponse {
  id: string;
  request_id: string;
}

export type AiDraftToolNameWire = 'assessment' | 'intake' | 'email' | 'brand_formatter' | 'med_list';

export interface AiDraftSummaryWire {
  id: string;
  tool_name: AiDraftToolNameWire;
  status: 'draft' | 'finalized' | 'archived';
  /** The linked client, or null for a free-text draft. */
  client_id: string | null;
  created_at: string;
  preview: string;
  // X.3.4.11: author display name, present only on the owner all-view
  // (null on own-view). Drives the attribution chip in DraftsList.
  created_by_name: string | null;
}

export interface ListDraftsResponse {
  rows: AiDraftSummaryWire[];
  request_id: string;
}

/**
 * X.3.4.7 single-draft detail wire shape (GET + PATCH responses). The
 * email-specific annotations + billing_suggestion live inside
 * input_payload (the save handler bundles them there; see
 * api/src/functions/aiToolsEmailSaveDraft.ts). Older drafts may omit them,
 * hence optional.
 */
export interface AiDraftDetailWire {
  id: string;
  tool_name: AiDraftToolNameWire;
  status: 'draft' | 'finalized' | 'archived';
  /** The linked client, or null for a free-text draft. */
  client_id: string | null;
  input_payload: {
    form_input?: {
      recipient?: string;
      topic?: string;
      constraints?: string;
      tone?: 'warmer' | 'standard' | 'formal'; // B2
    };
    annotations?: EmailAnnotation[];
    billing_suggestion?: EmailBillingSuggestion;
    // X.3.4.10: 'spice' drafts hide billing in the detail view. Absent on
    // pre-SPICE drafts → treated as 'care_management'. 'letter' marks an
    // Assessment Letter saved under the assessment tool.
    mode?: 'care_management' | 'spice' | 'letter';
    // SPICE email drafts: the medical or care content flag, shown above the email.
    medical_flag?: string | null;
    // Medication List drafts: the pasted source (if any) and the reviewed
    // document the tool resumes from.
    source_text?: string | null;
    document?: MedListDraftDocumentWire;
    [key: string]: unknown;
  };
  output_text: string;
  /** null only on a med_list draft that never called the model. */
  ai_invocation_id: number | null;
  finalized_at: string | null;
  created_at: string;
  updated_at: string;
}

export interface GetDraftResponse {
  draft: AiDraftDetailWire;
  request_id: string;
}

export interface UpdateDraftInput {
  email_body?: string;
  // X.3.4.8: 'archived' dropped. Delete is now a hard delete via
  // aiToolsApi.deleteDraft; the only status transition is 'finalized'.
  status?: 'finalized';
}

export interface UpdateDraftResponse {
  draft: AiDraftDetailWire;
  request_id: string;
}

// ---- X.3.5 Initial Assessment Drafter wire shapes ----
export type AdlIadlLevel = 'Independent' | 'Partial Assist' | 'Dependent' | 'Not assessed';

export interface AdlIadlEntry {
  activity: string;
  level: AdlIadlLevel;
  notes: string;
}

/**
 * Gap-interview tiers (2026-07 redesign). Classified server-side at
 * generation against this closed set; safety leads the review surface.
 * Optional on the wire so drafts saved before the redesign still render
 * (absent -> treated as 'completeness' in the UI).
 */
export type AssessmentFlagTier = 'safety' | 'completeness' | 'administrative';

export interface AssessmentFollowUpQuestion {
  section: string;
  question: string;
  tier?: AssessmentFlagTier;
}

/** One answered gap flag, sent to the patch pass ("Fill these in"). */
export interface AssessmentGapAnswer {
  section: string;
  question: string;
  answer: string;
}

export interface AssessmentBillingSuggestion {
  hours: number;
  category: string;
  rationale: string;
}

export interface AssessmentReportHeader {
  date: string;
  to: string;
  from: string;
  re: string;
}

// Meds/providers-as-tables (prompt v8, 2026-07): one row of the rendered
// Medications table. Unknown fields are '' (never omitted). Keep in sync
// with MedicationRow in the api schema.
export interface MedicationRow {
  name: string;
  dose: string;
  frequency: string;
  reason: string;
}

/** One row of the rendered Providers table (prompt v8). */
export interface ProviderRow {
  name: string;
  address: string;
  specialty: string;
  phone: string;
  email: string;
}

// X.3.5.2: medical_assessment broke out from a single string into a
// structured object (overview + required sub-sections + optional
// Insurance/Pharmacy), mirroring neurological. Keep in sync with the api
// schema's MedicalAssessmentSection.
export interface MedicalAssessmentSection {
  overview: string;
  home_care_status: string;
  past_surgical_history: string;
  recent_hospitalization_history: string;
  insurance?: string;
  pharmacy?: string;
  // Prompt v8: the structured medications/providers table rows. Optional
  // on the wire (legacy saved drafts predate them; the server emits them
  // on every parse, absent -> []). Non-empty arrays render as tables;
  // empty/absent falls back to the legacy in-overview bulleted lists.
  medications?: MedicationRow[];
  providers?: ProviderRow[];
}

export interface AssessmentReportSections {
  history: string;
  medical_assessment: MedicalAssessmentSection;
  neurological: {
    presentation: string;
    memory_cognition: string;
    mood_behavior: string;
    neuro_medical: string;
  };
  sensory: string;
  sleep: string;
  pain: string;
  cardiovascular: string;
  respiratory: string;
  gastroenterological: string;
  // Absent on a draft saved before the section existed; reads as empty.
  urinary: string;
  musculoskeletal: string;
  integumentary: string;
  endocrine: string;
  functional_assessment: string;
  social_assessment: string;
  home_safety_assessment: string;
  // Items 5/6 (2026-06): structured — a one-paragraph clinical `synthesis` plus
  // `recommendations`, each a short bold `label` + the what/why/next-step `body`.
  summary_and_recommendations: SummaryAndRecommendations;
}

export interface SummaryRecommendation {
  label: string;
  body: string;
}

export interface SummaryAndRecommendations {
  synthesis: string;
  recommendations: SummaryRecommendation[];
}

export interface AssessmentDraftResponse {
  ai_invocation_id: number;
  header: AssessmentReportHeader;
  sections: AssessmentReportSections;
  adl_iadl: AdlIadlEntry[];
  // Item 7 (2026-06): server-templated courtesy sentence rendered before the
  // signature (client name + resolved pronoun). Not model output.
  closing_courtesy: string;
  closing: string;
  // The server-built e-signature block beside `closing` (same identity as
  // the FROM line): the typed signature with "(electronically signed)", the
  // name with credentials, the designation. Absent on a draft saved before
  // the block existed; the closing text then carries the signature.
  signature?: ReportSignatureBlock;
  // Signature-integrity P0: true when the server could not resolve the
  // authenticated author's name (no staff_roster row / no real display
  // name). The report then ends at "Respectfully submitted," with NO name,
  // and the UI shows a plain-language add-your-name prompt.
  signatory_missing?: boolean;
  follow_up_questions: AssessmentFollowUpQuestion[];
  billing_suggestion: AssessmentBillingSuggestion;
  request_id: string;
  usage: {
    input_tokens: number;
    output_tokens: number;
    cache_creation_input_tokens: number;
    cache_read_input_tokens: number;
  };
}

export type AssessmentMode = 'care_management' | 'spice';

// X.3.5.4: the operator's pronoun selection. The three standard presets map to
// an explicit five-form set server-side (resolvePronouns); only 'custom' carries
// the operator-supplied forms. The model never derives pronouns.
export interface SpicePronounChoice {
  preset: 'she_her' | 'he_him' | 'they_them' | 'custom';
  custom?: {
    subject?: string;
    object?: string;
    possessive_determiner?: string;
    possessive_pronoun?: string;
    reflexive?: string;
  };
}

export interface AssessmentDraftInput {
  recipient: string;
  client_first_name: string;
  client_last_name: string;
  notes: string;
  client_age?: string;
  assessment_date?: string;
  assessment_location?: string;
  attendees?: string;
  sender_credentials?: string;
  // X.3.5.4: 'spice' swaps in the SPICE Assessment Report (different sections +
  // modality tables) and drops billing. Absent → 'care_management'.
  mode?: AssessmentMode;
  // Both modes; resolved server-side to the five forms.
  pronouns?: SpicePronounChoice;
}

// ---- X.3.5.4 SPICE Assessment Report wire shapes ----
export interface SpiceReportSections {
  spice_assessment: string;
  current_social_activity_status: string;
  family_educational_vocational_history: string;
  hobby_special_interest_inventory: string;
  sensorimotor_assessment: string;
  physical_assessment: string;
  memory_cognitive_assessment: string;
}

export interface SpiceSkillGoals {
  program_framing: string;
  leisure_skill_goals: string;
  cognitive_skill_goals: string;
  cognitive_strengths: string;
  physical_skill_goals: string;
}

export interface SpiceModalityTables {
  cognitive: string[];
  long_term_memory: string[];
  orientation: string[];
  physical: string[];
  toe_tappin_memories: string[];
}

export interface SpiceSummary {
  visit_rapport_recap: string;
  engagement_not_achievement: string;
  lets_engage: string;
}

/** The structured SPICE report (no billing; mirrors the api schema). */
export interface SpiceAssessmentReport {
  header: AssessmentReportHeader;
  salutation: string;
  opening: string;
  sections: SpiceReportSections;
  skill_goals: SpiceSkillGoals;
  // X.3.5.4 fold-in: client-specific lead-in under the modalities heading.
  modality_framing: string;
  modalities: SpiceModalityTables;
  summary: SpiceSummary;
  sign_off: string;
  // Signature-integrity P0: true when the server could not resolve the
  // authenticated assessor's name; the sign_off then ends at "Sincerely
  // yours," with NO name and the UI shows the add-your-name prompt.
  signatory_missing?: boolean;
  follow_up_questions: AssessmentFollowUpQuestion[];
}

export interface SpiceAssessmentDraftResponse extends SpiceAssessmentReport {
  ai_invocation_id: number;
  request_id?: string;
  usage: {
    input_tokens: number;
    output_tokens: number;
    cache_creation_input_tokens: number;
    cache_read_input_tokens: number;
  };
}

export interface AssessmentFormInputSnapshot {
  recipient: string;
  client_first_name: string;
  client_last_name: string;
  notes: string;
  client_age?: string;
  assessment_date?: string;
  assessment_location?: string;
  attendees?: string;
  sender_credentials?: string;
  /** The pronoun choice in effect on the page. A generation always records
   *  one (they/them when none was chosen in Care Management and the Letter);
   *  absent on a saved draft means no choice was recorded. */
  pronouns?: SpicePronounChoice;
  /** The pronoun choice the report prose was last written with. */
  report_pronouns?: SpicePronounChoice;
}

/** A vetted recommendation's library entry: its topic and its number within
 *  the topic (the seed's own identity; the row id changes on a re-seed). */
export interface RecommendationRef {
  topic_slug: string;
  entry_ordinal: number;
}

/** How the care manager curated a library entry when she added it. */
export interface RecommendationCuration {
  /** One per seeded sub-item: included or not. */
  sub_selected: boolean[];
  /** Her own bullets, kept verbatim. */
  extra_items: string[];
  /** One per option of a multi-option closer: included or not. */
  options_selected: boolean[];
  /** A single closer line: included or not. */
  include_closer: boolean;
}

export interface SaveAssessmentDraftInput {
  ai_invocation_id: number;
  /** The picked client, or null for free text (where Save to client documents files it). */
  client_id?: string | null;
  // X.3.5.4: optional; absent → 'care_management' on the server.
  mode?: 'care_management';
  header: AssessmentReportHeader;
  sections: AssessmentReportSections;
  adl_iadl: AdlIadlEntry[];
  // Item 7 (2026-06): round-tripped so it persists in structured_response.
  closing_courtesy: string;
  closing: string;
  // Round-tripped so the download renders the same e-signature block.
  signature?: ReportSignatureBlock;
  // Signature-integrity P0: round-tripped so the saved draft remembers the
  // report is unsigned.
  signatory_missing?: boolean;
  follow_up_questions: AssessmentFollowUpQuestion[];
  billing_suggestion: AssessmentBillingSuggestion;
  form_input: AssessmentFormInputSnapshot;
  // Assessment Drafter v2: vetted recommendations the CM clicked "Add to
  // report" on, in order — each with its topic (rendered as a branded
  // subheading) + assembled body (both already server-resolved by the F2
  // resolver). `needs_resources` (directory_generic closers) drives the
  // [ADD RESOURCES HERE] marker. Omitted by copy-out-era callers; defaults [].
  // ref, curation and worded_with let a reopened draft re-word an added
  // recommendation under another pronoun choice; absent on one added before
  // they were recorded.
  recommended_resources?: Array<{
    topic: string;
    body: string;
    needs_resources: boolean;
    ref?: RecommendationRef;
    curation?: RecommendationCuration;
    worded_with?: SpicePronounChoice;
  }>;
}

// X.3.5.4: SPICE save shape (no billing; the SPICE report blocks). `mode` is
// required so the server picks the SPICE validator + template + filename.
export interface SaveSpiceAssessmentDraftInput {
  ai_invocation_id: number;
  client_id?: string | null;
  mode: 'spice';
  header: AssessmentReportHeader;
  salutation: string;
  opening: string;
  sections: SpiceReportSections;
  skill_goals: SpiceSkillGoals;
  modality_framing: string;
  modalities: SpiceModalityTables;
  summary: SpiceSummary;
  sign_off: string;
  // Signature-integrity P0: round-tripped so the saved draft remembers the
  // report is unsigned.
  signatory_missing?: boolean;
  follow_up_questions: AssessmentFollowUpQuestion[];
  form_input: AssessmentFormInputSnapshot;
}

export interface SaveAssessmentDraftResponse {
  id: string;
  request_id: string;
}

// A reopened draft's Save: the same report content, written back to the
// row it came from. The row keeps its ai_invocation_id.
export type UpdateAssessmentDraftInput =
  | Omit<SaveAssessmentDraftInput, 'ai_invocation_id'>
  | Omit<SaveSpiceAssessmentDraftInput, 'ai_invocation_id'>;

/**
 * Second-round edits: refine (make changes and run again) for the Care
 * Management assessment report. current_report is the report as it reads
 * on screen, INCLUDING any inline section edits; the server validates it
 * with the canonical response parser, asks Opus to revise (not
 * regenerate), and returns the full AssessmentDraftResponse shape with a
 * NEW ai_invocation_id. The CM-curated recommended_resources never ride
 * along (the page keeps its own state and persists them at save). SPICE
 * reports are not supported yet (server rejects mode='spice').
 */
export interface AssessmentRefineReportPayload {
  header: AssessmentReportHeader;
  sections: AssessmentReportSections;
  adl_iadl: AdlIadlEntry[];
  closing_courtesy: string;
  closing: string;
  // Round-tripped so the server carries the block through the revision
  // (server-owned; never model-drafted).
  signature?: ReportSignatureBlock;
  // Signature-integrity P0: round-tripped so the server carries the
  // unsigned marker through the revision (server-owned; never model-drafted).
  signatory_missing?: boolean;
  follow_up_questions: AssessmentFollowUpQuestion[];
  billing_suggestion: AssessmentBillingSuggestion;
}

export interface AssessmentRefineInput {
  /** Free-form change request; may be '' when gap_answers drives the run. */
  instruction: string;
  /**
   * Gap interview ("Fill these in"): answered flags for the targeted patch
   * pass. Present-and-non-empty switches the server to the gap-patch
   * prompt (only the affected sections change; answered flags resolve).
   */
  gap_answers?: AssessmentGapAnswer[];
  current_report: AssessmentRefineReportPayload;
  form_input: AssessmentFormInputSnapshot;
}

// ---- Assessment Letter wire shapes ----
// The family letter sent after an assessment visit in place of the formal
// report. Model content (salutation, opening, recommendations, closing,
// valediction, follow-ups) plus the server-stamped identity surfaces (the
// header and the e-signature block). Mirrors the api schema
// assessmentLetterResponse.ts.
export interface AssessmentLetterRecommendation {
  /** Short bold run-in heading, e.g. "Elder Law Attorney". */
  heading: string;
  /** The first paragraph continues the heading on the same line. */
  paragraphs: string[];
  /** Named resources as bullets (contact details only as the CM supplied them). */
  resources: string[];
  /** A numbered sequence, when the source describes one. */
  steps: string[];
}

// The e-signature block the Initial Assessment report and the Assessment
// Letter end with (mirrors the api schema reportSignatureBlock.ts).
export interface ReportSignatureBlock {
  /** The signer's name alone (14pt italic in the .docx); '' when unsigned. */
  name_line: string;
  /** "(electronically signed)" beside the name; '' when unsigned. */
  esign_line: string;
  credentials_line: string;
  designation_line: string;
}

export interface AssessmentLetterBody {
  header: AssessmentReportHeader;
  salutation: string;
  opening: string[];
  recommendations: AssessmentLetterRecommendation[];
  closing: string[];
  valediction: string;
  signature: ReportSignatureBlock;
  // True when the server could not resolve the author's name; the letter
  // then ends at the valediction and the UI shows the add-your-name prompt.
  signatory_missing?: boolean;
  follow_up_questions: AssessmentFollowUpQuestion[];
}

export interface AssessmentLetterDraftResponse extends AssessmentLetterBody {
  ai_invocation_id: number;
  request_id?: string;
  usage: {
    input_tokens: number;
    output_tokens: number;
    cache_creation_input_tokens: number;
    cache_read_input_tokens: number;
  };
}

/** A vetted recommendation the CM picked from the library before generating. */
export interface AssessmentLetterRecommendationPick {
  topic: string;
  text: string;
}

export interface AssessmentLetterDraftInput {
  client_first_name: string;
  client_last_name: string;
  /** One recipient per line (the TO block). */
  recipients: string;
  re: string;
  /** ISO date; absent -> today on the server. */
  letter_date?: string;
  notes: string;
  pronouns?: SpicePronounChoice;
  recommendation_picks: AssessmentLetterRecommendationPick[];
}

/** Letter save shape: persists under the assessment tool with mode 'letter'. */
export interface SaveAssessmentLetterDraftInput extends AssessmentLetterBody {
  ai_invocation_id: number;
  client_id?: string | null;
  mode: 'letter';
  form_input: AssessmentFormInputSnapshot;
}

// ---- X.3.8 Brand Formatter wire shapes ----
// The model derives an ordered list of typed content blocks from arbitrary
// pasted text; the BrandedDocument.docx template renders them. The kind set
// is closed (mirrors the api schema brandFormatterResponse.ts). No billing.
// A table block carries rows (the first row is the header) instead of text;
// the two signature kinds are the name line and the "Electronically signed"
// line at the foot of a report.
export type BrandBlockKind =
  | 'heading'
  | 'subheading'
  | 'paragraph'
  | 'bullet'
  | 'numbered'
  | 'table'
  | 'signature_name'
  | 'signature_note';

export interface BrandBlock {
  kind: BrandBlockKind;
  text: string;
  rows?: string[][];
}

export interface BrandFormatDraftResponse {
  ai_invocation_id: number;
  title: string;
  blocks: BrandBlock[];
  request_id: string;
  usage: {
    input_tokens: number;
    output_tokens: number;
    cache_creation_input_tokens: number;
    cache_read_input_tokens: number;
  };
}

export interface BrandFormatDraftInput {
  source_text: string;
  title?: string;
  // C2: brand for the eventual download. Generation is brand-neutral; absent
  // → 'peace'. Sent on generate for API consistency, consumed at download.
  mode?: 'peace' | 'spice';
}

export interface BrandFormatFormInputSnapshot {
  title?: string;
}

export interface SaveBrandFormatDraftInput {
  ai_invocation_id: number;
  title: string;
  blocks: BrandBlock[];
  form_input: BrandFormatFormInputSnapshot;
  // C2: persisted in input_payload so the download fills the right brand
  // template. Absent → 'peace' (server default).
  mode?: 'peace' | 'spice';
}

export interface SaveBrandFormatDraftResponse {
  id: string;
  request_id: string;
}

// ---- Medication List wire shapes (med_list tool) ----
// The paste path: the model parses pasted text into flat medication rows; the
// FE buckets them (src/lib/medList.ts) into the company template's five
// time-of-day sections. The record path never hits generate (it assembles +
// buckets client-side). The download is a stateless POST of the reviewed
// bucketed document; a SAVED list lives in ai_drafts (tool_name med_list,
// optionally linked to a client) and downloads by id.
export interface MedListDraftMedication {
  medication: string;
  dose: string;
  method: string;
  purpose: string;
  schedule: string;
  notes: string;
}

export interface MedListDraftResponse {
  ai_invocation_id: number;
  medications: MedListDraftMedication[];
  usage: {
    input_tokens: number;
    output_tokens: number;
    cache_creation_input_tokens: number;
    cache_read_input_tokens: number;
  };
}

/** One rendered column row of the reviewed list (hand-mirror of the zod). */
export interface MedListDocumentRowWire {
  medication: string;
  /** The Prescribed by column (LP-32); '' when none. */
  prescriber: string;
  dose: string;
  method: string;
  purpose: string;
  notes: string;
}

/**
 * The reviewed, bucketed medication list: what the stateless download takes,
 * and what a med_list draft persists in input_payload.document. `date` is a
 * calendar day (YYYY-MM-DD; the server defaults an absent one to today and
 * refuses free text or a future day). The structured name is absent on a
 * draft saved before it existed.
 */
export interface MedListDraftDocumentWire {
  client_name: string | null;
  client_last_name?: string | null;
  client_first_name?: string | null;
  date: string | null;
  allergies: string;
  buckets: {
    morning: MedListDocumentRowWire[];
    afternoon: MedListDocumentRowWire[];
    evening: MedListDocumentRowWire[];
    bedtime: MedListDocumentRowWire[];
    as_needed: MedListDocumentRowWire[];
  };
  /** The rows the blocks were built from; absent on a draft saved before
   *  rows existed (hand mirror of the server's item schema). */
  items?: {
    name: string;
    dose: string;
    times: ('morning' | 'afternoon' | 'evening' | 'bedtime' | 'as_needed')[];
    directions: string;
    status: 'active' | 'discontinued';
    prescriber: string;
    reason: string;
    method: string;
  }[];
  /** The linked record's changes still to come (LP-41), printed under
   *  Changes coming; absent on a draft saved before the section existed. */
  changes_coming?: {
    medication: string;
    dose: string;
    change: 'starts' | 'stops';
    on: string;
  }[];
}

/** PUT body: content + the client link (uuid to link, null = free text),
 *  and the model call that produced the current rows (relinks the draft;
 *  absent or null leaves the stored link). */
export interface UpdateMedListDraftInput {
  client_id: string | null;
  source_text: string | null;
  document: MedListDraftDocumentWire;
  ai_invocation_id?: number | null;
}

/** POST body: the update body with the invocation required (the paste
 *  parse or the photo read; null when the list was typed or assembled from
 *  the record without a model call). */
export interface SaveMedListDraftInput extends UpdateMedListDraftInput {
  ai_invocation_id: number | null;
}

export interface SaveMedListDraftResponse {
  id: string;
  request_id: string;
}

export interface UpdateMedListDraftResponse {
  draft: AiDraftDetailWire;
  request_id: string;
}

// ---- F2 Recommendations library wire shapes ----
export type RecommendationCloserType = 'none' | 'baked_in' | 'directory_generic';

export interface RecommendationItem {
  id: string;
  topic: string;
  topic_slug: string;
  /** The entry's number within its topic; with topic_slug, the entry's
   *  identity across re-seeds. The server sends it on every item; an item
   *  without one is added as plain text that never re-words. */
  entry_ordinal?: number;
  closer_type: RecommendationCloserType;
  /** Fully resolved (name + pronouns substituted) copy-ready paragraph(s). */
  body: string;
  /** Resolved closer line, or null when closer_type is 'none'. */
  closer_text: string | null;
  /** Resolved bulleted sub-list items (empty when the entry has none). */
  sub_items: string[];
}

export interface RecommendationsResponse {
  items: RecommendationItem[];
}

export const aiToolsApi = {
  draftEmail(
    slug: string,
    input: EmailDraftInput,
    signal?: AbortSignal,
  ): Promise<EmailDraftResponse> {
    // X.3.5.1: NDJSON streaming endpoint (keeps the connection alive past
    // the SWA edge idle cap). Returns the same EmailDraftResponse shape.
    return postNdjson<EmailDraftResponse>(
      slug,
      '/ai-tools/email/draft',
      input as unknown as Record<string, unknown>,
      signal,
    );
  },

  // Second-round edits: revise the current draft (hand edits included)
  // per the operator's change request. Same NDJSON streaming transport +
  // response shape as draftEmail, new ai_invocation_id.
  refineEmail(
    slug: string,
    input: EmailRefineInput,
    signal?: AbortSignal,
  ): Promise<EmailDraftResponse> {
    return postNdjson<EmailDraftResponse>(
      slug,
      '/ai-tools/email/refine',
      input as unknown as Record<string, unknown>,
      signal,
    );
  },

  saveEmailDraft(
    slug: string,
    input: SaveEmailDraftInput,
    signal?: AbortSignal,
  ): Promise<SaveEmailDraftResponse> {
    return post<SaveEmailDraftResponse>(
      slug,
      '/ai-tools/email/drafts',
      input as unknown as Record<string, unknown>,
      signal,
    );
  },

  listDrafts(
    slug: string,
    opts: {
      scope?: 'own' | 'all';
      /** Only drafts linked to this client. */
      client_id?: string;
      /** Only one tool's drafts. */
      tool?: AiDraftToolNameWire;
      signal?: AbortSignal;
    } = {},
  ): Promise<ListDraftsResponse> {
    const qs = new URLSearchParams();
    if (opts.scope === 'all') qs.set('scope', 'all');
    if (opts.client_id) qs.set('client_id', opts.client_id);
    if (opts.tool) qs.set('tool', opts.tool);
    const query = qs.toString();
    const path = query.length > 0 ? `/ai-tools/drafts?${query}` : '/ai-tools/drafts';
    return get<ListDraftsResponse>(slug, path, opts.signal);
  },

  getDraft(slug: string, id: string, signal?: AbortSignal): Promise<GetDraftResponse> {
    return get<GetDraftResponse>(slug, `/ai-tools/drafts/${encodeURIComponent(id)}`, signal);
  },

  updateDraft(
    slug: string,
    id: string,
    input: UpdateDraftInput,
    signal?: AbortSignal,
  ): Promise<UpdateDraftResponse> {
    return patch<UpdateDraftResponse>(
      slug,
      `/ai-tools/drafts/${encodeURIComponent(id)}`,
      input as unknown as Record<string, unknown>,
      signal,
    );
  },

  // X.3.4.8: hard-delete a saved draft (Archive -> Delete refactor).
  // Resolves to void on the server's 204. Callers invalidate the
  // ['ai-tools','drafts',slug] list query on success.
  deleteDraft(slug: string, id: string, signal?: AbortSignal): Promise<void> {
    return del(slug, `/ai-tools/drafts/${encodeURIComponent(id)}`, signal);
  },

  // ---- X.3.5 Initial Assessment Drafter ----
  // X.3.5.4: the response is mode-dependent — Care Management returns the
  // clinical assessment shape, SPICE returns the SPICE report shape. The
  // caller narrows on its own `mode` (the wire carries no discriminant, so a
  // union return + caller-side narrowing keeps the surface honest).
  draftAssessment(
    slug: string,
    input: AssessmentDraftInput,
    signal?: AbortSignal,
  ): Promise<AssessmentDraftResponse | SpiceAssessmentDraftResponse> {
    // X.3.5.1: NDJSON streaming endpoint. Returns the same shape; the long
    // Opus generation no longer 500s at the SWA edge idle cap.
    return postNdjson<AssessmentDraftResponse | SpiceAssessmentDraftResponse>(
      slug,
      '/ai-tools/assessment/draft',
      input as unknown as Record<string, unknown>,
      signal,
    );
  },

  // Second-round edits: revise the current Care Management report (inline
  // section edits included) per the CM's change request. Same NDJSON
  // streaming transport + response shape as draftAssessment (CM branch).
  refineAssessment(
    slug: string,
    input: AssessmentRefineInput,
    signal?: AbortSignal,
  ): Promise<AssessmentDraftResponse> {
    return postNdjson<AssessmentDraftResponse>(
      slug,
      '/ai-tools/assessment/refine',
      input as unknown as Record<string, unknown>,
      signal,
    );
  },

  // ---- Assessment Letter ----
  // Same NDJSON streaming transport as draftAssessment; returns the letter
  // shape with the server-stamped header + e-signature block.
  draftAssessmentLetter(
    slug: string,
    input: AssessmentLetterDraftInput,
    signal?: AbortSignal,
  ): Promise<AssessmentLetterDraftResponse> {
    return postNdjson<AssessmentLetterDraftResponse>(
      slug,
      '/ai-tools/assessment-letter/draft',
      input as unknown as Record<string, unknown>,
      signal,
    );
  },

  saveAssessmentDraft(
    slug: string,
    input: SaveAssessmentDraftInput | SaveSpiceAssessmentDraftInput | SaveAssessmentLetterDraftInput,
    signal?: AbortSignal,
  ): Promise<SaveAssessmentDraftResponse> {
    return post<SaveAssessmentDraftResponse>(
      slug,
      '/ai-tools/assessment/drafts',
      input as unknown as Record<string, unknown>,
      signal,
    );
  },

  /** Replace a saved assessment draft's content in place (a reopened draft's Save). */
  updateAssessmentDraft(
    slug: string,
    id: string,
    input: UpdateAssessmentDraftInput,
    signal?: AbortSignal,
  ): Promise<SaveAssessmentDraftResponse> {
    return put<SaveAssessmentDraftResponse>(
      slug,
      `/ai-tools/assessment/drafts/${encodeURIComponent(id)}`,
      input as unknown as Record<string, unknown>,
      signal,
    );
  },

  /**
   * Build the .docx download URL for a saved assessment draft. The
   * download is a GET the browser hits directly (anchor href /
   * window.open), so it returns a URL string rather than fetching; the
   * server sets Content-Disposition for the filename.
   */
  assessmentDownloadUrl(slug: string, id: string): string {
    return `/api/t/${encodeURIComponent(slug)}/v1/ai-tools/assessment/drafts/${encodeURIComponent(id)}/download`;
  },

  /**
   * File the saved draft's Word file (exactly the download) as a document
   * of its client. A second save files a new version superseding the
   * first ('replaced'); a save while the last copy is still saving is
   * refused (409 save_in_progress).
   */
  saveAssessmentToDocuments(
    slug: string,
    id: string,
    signal?: AbortSignal,
  ): Promise<{ document_id: string; client_id: string; version: 'new' | 'replaced' }> {
    return post<{ document_id: string; client_id: string; version: 'new' | 'replaced' }>(
      slug,
      `/ai-tools/assessment/drafts/${encodeURIComponent(id)}/save-to-documents`,
      {},
      signal,
    );
  },

  // ---- F2 Recommendations library ----
  // Returns the full vetted library with every entry's placeholders resolved
  // server-side against the supplied client context (first name + pronoun
  // preset). The picker only ever displays what the server produced.
  listRecommendations(
    slug: string,
    input: { client_first_name: string; pronouns?: SpicePronounChoice },
    signal?: AbortSignal,
  ): Promise<RecommendationsResponse> {
    return post<RecommendationsResponse>(
      slug,
      '/ai-tools/recommendations',
      input as unknown as Record<string, unknown>,
      signal,
    );
  },

  // ---- X.3.8 Brand Formatter ----
  draftBrandFormat(
    slug: string,
    input: BrandFormatDraftInput,
    signal?: AbortSignal,
  ): Promise<BrandFormatDraftResponse> {
    // NDJSON streaming endpoint (heartbeat keeps the connection alive past the
    // SWA edge idle cap); returns { ai_invocation_id, title, blocks, usage }.
    return postNdjson<BrandFormatDraftResponse>(
      slug,
      '/ai-tools/brand-formatter/draft',
      input as unknown as Record<string, unknown>,
      signal,
    );
  },

  saveBrandFormatDraft(
    slug: string,
    input: SaveBrandFormatDraftInput,
    signal?: AbortSignal,
  ): Promise<SaveBrandFormatDraftResponse> {
    return post<SaveBrandFormatDraftResponse>(
      slug,
      '/ai-tools/brand-formatter/drafts',
      input as unknown as Record<string, unknown>,
      signal,
    );
  },

  /**
   * Build the .docx download URL for a saved Brand Formatter draft. The
   * download is a GET the browser hits directly (anchor href), so it returns
   * a URL string; the server sets Content-Disposition for the filename.
   */
  brandFormatDownloadUrl(slug: string, id: string): string {
    return `/api/t/${encodeURIComponent(slug)}/v1/ai-tools/brand-formatter/drafts/${encodeURIComponent(id)}/download`;
  },

  // ---- Medication List builder (med_list) ----
  /** Paste path: parse a pasted med list into flat rows (the FE buckets them). */
  draftMedList(
    slug: string,
    /** A paste, or a PDF (base64) read under the same guidance. */
    input: { source_text: string } | { source_pdf: { data_base64: string; file_name: string } },
    signal?: AbortSignal,
  ): Promise<MedListDraftResponse> {
    // NDJSON streaming endpoint (heartbeat past the SWA idle cap); returns
    // { ai_invocation_id, medications, usage }.
    return postNdjson<MedListDraftResponse>(
      slug,
      '/ai-tools/med-list/draft',
      input as unknown as Record<string, unknown>,
      signal,
    );
  },

  /**
   * The stateless .docx download URL for the reviewed medication list. The
   * client POSTs the reviewed { client_name, date, allergies, buckets } body to
   * this endpoint (see downloadDocxPost); the server renders + returns the .docx
   * with a Content-Disposition filename. A SAVED list downloads by id instead
   * (medListDraftDownloadUrl).
   */
  medListDownloadUrl(slug: string): string {
    return `/api/t/${encodeURIComponent(slug)}/v1/ai-tools/med-list/download`;
  },

  /** Save the reviewed list to the drafts inbox (optionally linked to a client). */
  saveMedListDraft(
    slug: string,
    input: SaveMedListDraftInput,
    signal?: AbortSignal,
  ): Promise<SaveMedListDraftResponse> {
    return post<SaveMedListDraftResponse>(
      slug,
      '/ai-tools/med-list/drafts',
      input as unknown as Record<string, unknown>,
      signal,
    );
  },

  /** Replace a saved list's content and client link (link after the fact rides here). */
  updateMedListDraft(
    slug: string,
    id: string,
    input: UpdateMedListDraftInput,
    signal?: AbortSignal,
  ): Promise<UpdateMedListDraftResponse> {
    return put<UpdateMedListDraftResponse>(
      slug,
      `/ai-tools/med-list/drafts/${encodeURIComponent(id)}`,
      input as unknown as Record<string, unknown>,
      signal,
    );
  },

  /** The .docx download URL for a SAVED medication list (GET by id). */
  medListDraftDownloadUrl(slug: string, id: string): string {
    return `/api/t/${encodeURIComponent(slug)}/v1/ai-tools/med-list/drafts/${encodeURIComponent(id)}/download`;
  },
};

// =====================================================================
// Announcements (Phase X.5.1) — Olive hub banner
// =====================================================================
//
// GET is the read surface (hub banner; gated server-side on
// view_staff_tools). POST / PATCH / DELETE are manage_announcements-gated
// server-side (re-derived from ctx.role). The list is server-computed
// "active" (non-recurring past rows excluded; annual rows rolled forward;
// sorted by effective_date) — the client renders the returned order as-is.

export type AnnouncementType = 'event' | 'birthday' | 'anniversary' | 'holiday' | 'closed';
// X.5.2 (0084) widened from (none | annual). Mirrors api/_shared/schema.ts.
export type AnnouncementRecurrence = 'none' | 'annual' | 'monthly' | 'biweekly' | 'weekly';

/** Where a hub row comes from (mirrors api/_shared/repos/announcements.ts):
 *  a stored row someone wrote, or an occasion derived from the staff
 *  roster (a birthday or workiversary), which is never stored and is
 *  corrected on the People page, not here. */
export type AnnouncementSource = 'announcement' | 'roster';

/** A hub row's type: a stored type, or 'first_day', which only a
 *  roster-derived row carries (a start date inside the window). Mirrors
 *  api/_shared/repos/announcements.ts HubRowType. */
export type HubRowType = AnnouncementType | 'first_day';

export interface AnnouncementWire {
  id: string;
  type: HubRowType;
  body: string;
  event_date: string | null;
  recurrence: AnnouncementRecurrence;
  /** null for a roster-derived occasion. */
  created_by_user_id: string | null;
  created_at: string;
  /** Server-computed occurrence date used for filter + sort; null = dateless. */
  effective_date: string | null;
  source: AnnouncementSource;
}

export interface ListAnnouncementsResponse {
  announcements: AnnouncementWire[];
  /** X.5.2 — active announcements the caller has not yet seen. */
  unread_count: number;
  request_id: string;
}

export interface AnnouncementCreateInput {
  type: AnnouncementType;
  body: string;
  event_date: string | null;
  recurrence: AnnouncementRecurrence;
}

export interface AnnouncementCreateResponse {
  id: string;
  request_id: string;
}

export type AnnouncementUpdateInput = Partial<AnnouncementCreateInput>;

export const announcementsApi = {
  list(slug: string, signal?: AbortSignal): Promise<ListAnnouncementsResponse> {
    return get<ListAnnouncementsResponse>(slug, '/announcements', signal);
  },

  create(
    slug: string,
    input: AnnouncementCreateInput,
    signal?: AbortSignal,
  ): Promise<AnnouncementCreateResponse> {
    return post<AnnouncementCreateResponse>(
      slug,
      '/announcements',
      input as unknown as Record<string, unknown>,
      signal,
    );
  },

  update(
    slug: string,
    id: string,
    input: AnnouncementUpdateInput,
    signal?: AbortSignal,
  ): Promise<AnnouncementCreateResponse> {
    return patch<AnnouncementCreateResponse>(
      slug,
      `/announcements/${encodeURIComponent(id)}`,
      input as unknown as Record<string, unknown>,
      signal,
    );
  },

  // The server returns 200 { deleted: true } (not 204); the `del` helper
  // ignores the success body, so this resolves to void either way.
  remove(slug: string, id: string, signal?: AbortSignal): Promise<void> {
    return del(slug, `/announcements/${encodeURIComponent(id)}`, signal);
  },

  // X.5.2 — mark the caller as having seen announcements (clears unread).
  markSeen(slug: string, signal?: AbortSignal): Promise<{ seen: boolean; request_id: string }> {
    return post<{ seen: boolean; request_id: string }>(slug, '/announcements/seen', undefined, signal);
  },
};

// =====================================================================
// Workspace notification bell (PR3) — stored feed + computed overdue
// =====================================================================

export type WorkNotificationKind = 'assigned' | 'overdue' | 'mention';

/** A stored work_notifications row. */
export interface WorkNotificationWire {
  id: string;
  kind: WorkNotificationKind;
  title: string;
  work_item_id: string | null;
  seen_at: string | null;
  created_at: string;
}

/** A computed overdue work_item. `id` is the work_item id. */
export interface OverdueItemWire {
  id: string;
  title: string;
  due_date: string;
  kind: 'intake' | 'task';
  stage_id: string | null;
}

export interface ListWorkNotificationsResponse {
  notifications: WorkNotificationWire[];
  overdue: OverdueItemWire[];
  /** Server-authoritative badge: overdue + unseen-stored-not-already-overdue. */
  unread_count: number;
  request_id: string;
}

export const workNotificationsApi = {
  list(slug: string, signal?: AbortSignal): Promise<ListWorkNotificationsResponse> {
    return get<ListWorkNotificationsResponse>(slug, '/work-notifications', signal);
  },

  // Mark the caller's unseen stored notifications seen (overdue is unaffected).
  markSeen(slug: string, signal?: AbortSignal): Promise<{ seen: boolean; request_id: string }> {
    return post<{ seen: boolean; request_id: string }>(slug, '/work-notifications/seen', undefined, signal);
  },
};

// =====================================================================
// Notifications (notifications part two) — the bell, the inbox, and
// each person's own rules over the catalogue (src/lib/notifications/
// catalogue.ts). This REPLACES the announcements + work-notifications
//'assigned' sources the header bell used to aggregate (see
// NotificationBell.tsx's header comment for the evidence + the
// decision); workNotificationsApi above is kept ONLY for the still-
// unique computed-overdue list, which has no catalogue equivalent.
// =====================================================================

export interface NotificationWire {
  id: string;
  event_key: string;
  resource_type: string | null;
  resource_id: string | null;
  title: string;
  summary: string;
  link: string;
  created_at: string;
  read_at: string | null;
}

export interface ListNotificationsResponse {
  rows: NotificationWire[];
  unread_count: number;
  request_id: string;
}

export interface NotificationRule {
  channel: 'email_and_in_app' | 'in_app' | 'off';
  cadence: 'immediate' | 'daily';
}

export interface EffectiveNotificationPreferenceRow {
  event_key: string;
  label: string;
  description: string;
  tenant_default: NotificationRule;
  own_rule: NotificationRule | null;
  effective: NotificationRule;
}

export interface NotificationPreferencesView {
  events: EffectiveNotificationPreferenceRow[];
  email_suppressed: boolean;
  request_id: string;
}

export interface TenantNotificationDefaultRow {
  event_key: string;
  label: string;
  description: string;
  channel: NotificationRule['channel'];
  cadence: NotificationRule['cadence'];
  is_tenant_set: boolean;
}

export const notificationsApi = {
  list(
    slug: string,
    opts: { unreadOnly?: boolean; limit?: number; offset?: number } = {},
    signal?: AbortSignal,
  ): Promise<ListNotificationsResponse> {
    const qs = api.buildQuery({
      unread: opts.unreadOnly ? '1' : undefined,
      limit: opts.limit,
      offset: opts.offset,
    });
    return get<ListNotificationsResponse>(slug, `/notifications${qs}`, signal);
  },

  unreadCount(slug: string, signal?: AbortSignal): Promise<{ unread_count: number; request_id: string }> {
    return get<{ unread_count: number; request_id: string }>(slug, '/notifications/unread-count', signal);
  },

  markRead(slug: string, id: string, signal?: AbortSignal): Promise<{ read: boolean; request_id: string }> {
    return post<{ read: boolean; request_id: string }>(slug, `/notifications/${id}/read`, undefined, signal);
  },

  markAllRead(slug: string, signal?: AbortSignal): Promise<{ marked: number; request_id: string }> {
    return post<{ marked: number; request_id: string }>(slug, '/notifications/read-all', undefined, signal);
  },
};

export const notificationPreferencesApi = {
  list(slug: string, signal?: AbortSignal): Promise<NotificationPreferencesView> {
    return get<NotificationPreferencesView>(slug, '/notification-preferences', signal);
  },

  set(
    slug: string,
    eventKey: string,
    rule: NotificationRule,
    signal?: AbortSignal,
  ): Promise<{ preference: unknown; request_id: string }> {
    return put<{ preference: unknown; request_id: string }>(
      slug,
      `/notification-preferences/${eventKey}`,
      rule as unknown as Record<string, unknown>,
      signal,
    );
  },

  reset(slug: string, eventKey: string, signal?: AbortSignal): Promise<{ reset: boolean; request_id: string }> {
    return delJson<{ reset: boolean; request_id: string }>(
      slug,
      `/notification-preferences/${eventKey}`,
      signal,
    );
  },
};

export const adminNotificationDefaultsApi = {
  list(
    slug: string,
    signal?: AbortSignal,
  ): Promise<{ defaults: TenantNotificationDefaultRow[]; request_id: string }> {
    return get<{ defaults: TenantNotificationDefaultRow[]; request_id: string }>(
      slug,
      '/admin/notification-defaults',
      signal,
    );
  },

  set(
    slug: string,
    defaults: Record<string, NotificationRule>,
    signal?: AbortSignal,
  ): Promise<{ defaults: TenantNotificationDefaultRow[]; request_id: string }> {
    return put<{ defaults: TenantNotificationDefaultRow[]; request_id: string }>(
      slug,
      '/admin/notification-defaults',
      { defaults },
      signal,
    );
  },
};

// migration 0185 -- the counties a unit covers. GET is the everyone-tier
// read the Team directory's per-unit line uses (view_staff_tools); PUT
// replaces one unit's whole set (manage_team).
export interface UnitCoverageRow {
  unit_id: string;
  unit_name: string;
  /** County display names, alphabetical. */
  counties: string[];
}

export const unitCoverageApi = {
  list(slug: string, signal?: AbortSignal): Promise<{ units: UnitCoverageRow[]; request_id: string }> {
    return get<{ units: UnitCoverageRow[]; request_id: string }>(slug, '/units/coverage', signal);
  },

  setCoverage(
    slug: string,
    unitId: string,
    areaIds: string[],
    signal?: AbortSignal,
  ): Promise<{ unit_id: string; counties: string[]; request_id: string }> {
    return put<{ unit_id: string; counties: string[]; request_id: string }>(
      slug,
      `/admin/units/${encodeURIComponent(unitId)}/coverage`,
      { area_ids: areaIds },
      signal,
    );
  },
};

// =====================================================================
// Workspace work-items (PR4a) — board / drawer / move-stage / checklist
// =====================================================================

export type WorkItemKind = 'intake' | 'task' | 'process_item';
export type WorkItemStatus = 'todo' | 'in_progress' | 'done';
export type WorkItemPriority = 'low' | 'normal' | 'high';
export type WorkItemEventKind =
  | 'comment'
  | 'checklist'
  | 'stage_change'
  | 'status_change'
  | 'assignment'
  | 'system';

/** A resolved collaborator (id + display name) — the card stack + drawer chip. */
export interface CollaboratorWire {
  user_id: string;
  name: string;
}

export interface WorkItemRow {
  id: string;
  tenant_id: string;
  kind: WorkItemKind;
  title: string;
  status: WorkItemStatus;
  stage_id: string | null;
  /** migration 0154 — the board this card belongs to. NOT NULL for intake +
   *  process_item; NULL for a task. Lets the drawer fetch the right process's
   *  stages + checklist grouping. */
  process_id: string | null;
  client_id: string | null;
  prospect_name: string | null;
  referral_org_id: string | null;
  referral_individual_id: string | null;
  referral_text: string | null;
  assignee_user_id: string | null;
  assignee_roster_id: string | null;
  due_date: string | null;
  /** The day to touch this card next ("Next action"). Distinct from the
   *  due_date deadline: a chase date must never read as overdue. Any kind;
   *  null = none planned. */
  next_action_date: string | null;
  priority: WorkItemPriority;
  description: string | null;
  /** WS-7 (0139) — category: a task's vocabulary grouping label
   *  (readTaskCategories) OR a process card's board field value (field
   *  pack v1, readProcessCardFields — e.g. a hiring board's role).
   *  notes = task-only free-text working notes (drawer). */
  category: string | null;
  /** A process card's full ordered value set (a talent candidate's several
   *  roles). category is always its first value; null on tasks/intakes and
   *  on a card with no value. */
  categories: string[] | null;
  notes: string | null;
  archived_at: string | null;
  // PR4b — intake-tracker fields.
  contact_name: string | null;
  contact_phone: string | null;
  contact_email: string | null;
  prospect_zip: string | null;
  contact_relationship: string | null;
  contact_is_poa: boolean;
  contact_date: string | null;
  service_requested: string | null;
  contact_method: string | null;
  paperwork_method: string | null;
  referrer_type: string | null;
  intake_call_by: string | null;
  unit_id: string | null; // Slice 3b — early geographic unit
  closed_at: string | null;
  close_reason: string | null;
  created_by_user_id: string;
  updated_by_user_id: string;
  created_at: string;
  updated_at: string;
  /** Slice 3a: CURRENT-STAGE + REQUIRED-only checklist progress for the card chip ("Client Setup · 4/10"). */
  checklist_done: number;
  checklist_total: number;
  current_stage_label: string | null; // the current stage's label (chip prefix); null for tasks
  // Slice 3b-2: read-resolved card fields — canonical name (clients.display_name once
  // converted, else prospect_name) + the unit name/dot color (resolves unit_id).
  display_name: string | null;
  /** LP-2: the linked client's name, every kind; null when unlinked. A task
   *  keeps its title as display_name, so the client is this field. Optional
   *  on the type so older fixtures stay valid; the server always sends it. */
  client_display_name?: string | null;
  unit_name: string | null;
  unit_color: string | null;
  /** PR4-fidelity: resolved assignee name for the card avatar (list-only; null when unassigned). */
  assignee_name: string | null;
  /** Shared assignment (0108): the card's collaborators (owner + collaborators
   *  model). The process OWNER (assignee_user_id) is never in this list. */
  collaborators: CollaboratorWire[];
  /** The caller's role on THIS card — set ONLY on a for_user (My Work) list:
   *  'owner' when assignee_user_id === for_user, else 'collaborator'. null otherwise. */
  my_role: 'owner' | 'collaborator' | null;
  /** Workspace v3 Phase 2: the live recurring-task instance's rule (null for
   *  non-recurring) + a plain-language cadence label for the agenda subline
   *  (list-only; null off-list). */
  recurrence_id: string | null;
  recurrence_summary: string | null;
  /** Conversion-view fix: a single resolved referral-source label (org name via
   *  the merge terminus, else individual name, else free text). List-only; null
   *  off-list and for tasks. Powers the Decided list's referral column. */
  referral_source_label: string | null;
  /** Assignment-card folder link (soak): the first allows_link checklist URL
   *  entered on this item (Template v2, 0124 — e.g. the client's prospect
   *  folder). List-only; null off-list. The Case Assignment card renders it. */
  folder_link_url: string | null;
  /** Card attachments (0162): how many ACTIVE files hang off this card.
   *  List-only (a grouped scalar sub-count, never an N+1); 0 off-list.
   *  Drives the card face's paperclip pill, which renders only above 0. */
  attachment_count: number;
}

export interface WorkItemChecklistItemRow {
  id: string;
  work_item_id: string;
  sort_order: number;
  label: string;
  owner_role: string | null;
  maps_to_stage_key: string | null;
  is_gate: boolean;
  is_done: boolean;
  done_by_user_id: string | null;
  done_at: string | null;
  source_template_item_id: string | null;
  link_key: string | null; // 0154 — rename-safe step anchor (deep-link consumers key on THIS, never the label)
  parent_id: string | null; // Slice 3a — self-ref nesting (sub-items)
  allows_note: boolean; // Slice 3a — item takes a free-text note
  allows_link: boolean; // Template v2 (0124) — item carries a URL
  is_optional: boolean; // Slice 3a — tier 3 "if applicable" (excluded from required progress)
  note: string | null; // Slice 3a — the per-item note value
  link_url: string | null; // Template v2 (0124) — the per-item URL value
  item_type: string; // 0159 — 'checkbox' | 'date' | 'text' (carried from template)
  value_text: string | null; // 0159 — filled value of a date/text item; filled = done
  created_at: string;
  updated_at: string;
}

export interface WorkItemEventRow {
  id: string;
  work_item_id: string;
  kind: WorkItemEventKind;
  body: string | null;
  actor_user_id: string;
  actor_name: string | null;
  metadata: Record<string, unknown> | null;
  edited_at: string | null;
  created_at: string;
  updated_at: string;
}

export interface WorkItemDetail extends WorkItemRow {
  checklist: WorkItemChecklistItemRow[];
  events: WorkItemEventRow[];
  warnings?: string[];
  referral_org_resolved_id?: string | null;
  /** PR4-fidelity: resolved names for the drawer; referral links to its Directory page. */
  referral_org_resolved_name?: string | null;
  referral_individual_name?: string | null;
  assignee_user_name?: string | null;
  assignee_roster_name?: string | null;
  /** Slice 3c: the owning CM's bio. NOT rendered in the drawer since 2026-07-26
   *  (a full biography swamped the "At a glance" card) — it is carried because
   *  it is a paragraph OF the intro email the Care manager row copies. */
  assignee_roster_bio?: string | null;
  /** The remaining renderIntroEmail inputs (0161), so the drawer's
   *  "Copy intro email" produces the same email the People tab does.
   *  null = not on file → the template renders a highlighted placeholder. */
  assignee_roster_credentials?: string | null;
  assignee_roster_preferred_name?: string | null;
  assignee_roster_email?: string | null;
  assignee_roster_phone?: string | null;
  /** LP-3: the linked client's SPICE therapist, for the drawer's row. */
  spice_therapist_roster_id?: string | null;
  spice_therapist_name?: string | null;
  /** Card attachments (0162 design §1.4): whether this card's PROCESS has
   *  its attachment surface on. Detail-only. The drawer hides the whole
   *  Attachments section unless this is explicitly true, so a board with
   *  the fence down (the intake board, by seeded default) shows nothing.
   *  Display only — every write path re-checks the flag server-side. */
  attachments_enabled?: boolean;
}

export interface ListWorkItemsResponse {
  rows: WorkItemRow[];
  total: number;
  limit: number;
  offset: number;
  request_id: string;
}

export type WorkItemBucket = 'active' | 'assigned' | 'closed';

/** My Tasks polish: a row in the agenda's "Completed" section (last 30 days).
 *  One-off done tasks (restorable) + recurring completion occurrences (the
 *  advance-in-place log rows; read-only). */
export interface CompletedTaskRow {
  /** Unique row key: the work_item id (one-off) or the completion event id (recurring). */
  id: string;
  work_item_id: string;
  title: string;
  completed_at: string; // ISO timestamp
  completed_for: string | null; // recurring occurrence date (YYYY-MM-DD); null for one-offs
  is_recurring: boolean;
  restorable: boolean;
}

export interface ListCompletedTasksResponse {
  rows: CompletedTaskRow[];
  request_id: string;
}

export interface ListWorkItemsParams {
  kind?: WorkItemKind;
  status?: WorkItemStatus;
  stage_key?: string;
  /** migration 0154 — scope a board read to one process (visibility-checked
   *  server-side). Absent = process_item rows are excluded (intake/task views
   *  unchanged); present = that process's cards (a non-intake board). */
  process?: string;
  assignee_user_id?: string;
  /** Shared assignment (0108) My Work — the caller's cards as owner OR collaborator. */
  for_user?: string;
  client_id?: string;
  include_archived?: boolean;
  include_closed?: boolean;
  bucket?: WorkItemBucket; // Slice 2 — terminal-bucket partition
  /** Conversion-view fix: Decided-list outcome filter (closed bucket only). */
  outcome?: 'converted' | 'closed_unconverted';
  q?: string; // Slice 2 — server-side name search (client + caller)
  /** WS-7 — exact-match category filter (server-side; pairs with the picker). */
  category?: string;
  /** WS-7 — server-side sort (the pagination lives there): due-date-soonest
   *  (overdue naturally first, NULLs last), category groups, or updated desc. */
  sort?: WorkItemSort;
  limit?: number;
  offset?: number;
}

export type WorkItemSort = 'due_date' | 'category' | 'recently_updated';

export interface ClientSetupInput {
  caretree_external_id?: string | null;
  first_name?: string | null;
  last_name?: string | null;
  /** clients.pronouns (0128 closed set). Optional — NULL = not recorded
   *  and NEVER blocks the move. */
  pronouns?: string | null;
  /** Add client's fields: the preferred name, the service (required by
   *  the route when the move creates the client) and a standard rate set
   *  with its start date. */
  preferred_name?: string | null;
  engagement?: 'care_management' | 'spice' | 'both';
  rate_set_id?: string;
  rate_effective_from?: string;
  /** "Use this client": link the card to an existing client instead of
   *  creating one. Sent alone: the route refuses it beside a create field. */
  link_client_id?: string;
}

export interface MoveStageInput {
  to_stage: string;
  client_setup?: ClientSetupInput;
  assignee_roster_id?: string | null;
  /** LP-3: the SPICE therapist to assign at the Assigned move. */
  spice_therapist_roster_id?: string | null;
  unit_ids?: string[];
  auto_complete_stage?: boolean; // Slice 3b — "Done · advance": tick the stage's required non-gate items in-tx
}

export interface CreateWorkItemInput {
  kind: WorkItemKind;
  title: string;
  // PR3 — process_item board target (required for kind='process_item').
  process_id?: string | null;
  stage_id?: string | null;
  prospect_name?: string | null;
  referral_org_id?: string | null;
  referral_individual_id?: string | null;
  referral_text?: string | null;
  due_date?: string | null;
  next_action_date?: string | null;
  priority?: WorkItemPriority;
  description?: string | null;
  // WS-7 — category: tasks (vocabulary-validated) + process_items (free
  // text, field pack v1); the server NULLs it for intakes. notes stays
  // task-only.
  category?: string | null;
  notes?: string | null;
  assignee_user_id?: string | null;
  /** LP-2: a task's client link (task-only; the server validates the client). */
  client_id?: string | null;
  // PR4b — capture-at-call intake fields.
  contact_name?: string | null;
  contact_phone?: string | null;
  contact_email?: string | null;
  prospect_zip?: string | null;
  contact_relationship?: string | null;
  contact_is_poa?: boolean;
  contact_date?: string | null;
  service_requested?: string | null;
  contact_method?: string | null;
  referrer_type?: string | null;
  intake_call_by?: string | null;
  unit_id?: string | null;
}

export type UpdateWorkItemInput = Partial<{
  title: string;
  status: WorkItemStatus;
  priority: WorkItemPriority;
  due_date: string | null;
  next_action_date: string | null;
  description: string | null;
  // WS-7 — task-only (the server rejects them on an intake).
  category: string | null;
  /** process_item only: the full value set; the server sets category to its first value. */
  categories: string[] | null;
  notes: string | null;
  assignee_user_id: string | null;
  assignee_roster_id: string | null;
  /** LP-2: link, change or clear (null) a task's client. */
  client_id: string | null;
  prospect_name: string | null;
  referral_org_id: string | null;
  referral_individual_id: string | null;
  referral_text: string | null;
  // PR4b — intake-tracker fields (close goes via the close action, not PATCH).
  contact_name: string | null;
  contact_phone: string | null;
  contact_email: string | null;
  prospect_zip: string | null;
  contact_relationship: string | null;
  contact_is_poa: boolean;
  contact_date: string | null;
  service_requested: string | null;
  contact_method: string | null;
  paperwork_method: string | null;
  referrer_type: string | null;
  intake_call_by: string | null;
  unit_id: string | null;
}>;

export type IntakeListKey =
  | 'service_requested'
  | 'contact_relationship'
  | 'contact_method'
  | 'paperwork_method'
  | 'close_reason'
  | 'referrer_type'
  | 'intake_call_by';

export interface IntakeOptions {
  lists: Record<IntakeListKey, string[]>;
  /** Forced pre-select per list (Slice 1: referrer_type → "Information Not Collected"). */
  defaults: Partial<Record<IntakeListKey, string>>;
  /** The option label that reveals a free-text box, per list (Slice 1: intake_call_by → "Other (Please Specify)"). */
  free_text: Partial<Record<IntakeListKey, string>>;
}

export const workItemsApi = {
  list(slug: string, params: ListWorkItemsParams, signal?: AbortSignal): Promise<ListWorkItemsResponse> {
    const q = api.buildQuery({
      kind: params.kind,
      status: params.status,
      stage_key: params.stage_key,
      process: params.process,
      assignee_user_id: params.assignee_user_id,
      for_user: params.for_user,
      client_id: params.client_id,
      include_archived: params.include_archived ? 'true' : undefined,
      include_closed: params.include_closed ? 'true' : undefined,
      bucket: params.bucket,
      outcome: params.outcome,
      q: params.q && params.q.trim() ? params.q.trim() : undefined,
      category: params.category,
      sort: params.sort,
      limit: params.limit,
      offset: params.offset,
    });
    return get<ListWorkItemsResponse>(slug, `/work-items${q}`, signal);
  },

  get(slug: string, id: string, signal?: AbortSignal): Promise<WorkItemDetail> {
    return get<WorkItemDetail>(slug, `/work-items/${encodeURIComponent(id)}`, signal);
  },

  /** My Tasks polish: the "Completed" section read (defaults to the caller server-side). */
  completedTasks(slug: string, params: { limit?: number } = {}, signal?: AbortSignal): Promise<ListCompletedTasksResponse> {
    const q = api.buildQuery({ limit: params.limit });
    return get<ListCompletedTasksResponse>(slug, `/completed-tasks${q}`, signal);
  },

  create(slug: string, input: CreateWorkItemInput, signal?: AbortSignal): Promise<WorkItemRow> {
    return post<WorkItemRow>(slug, '/work-items', input as unknown as Record<string, unknown>, signal);
  },

  update(slug: string, id: string, edits: UpdateWorkItemInput, signal?: AbortSignal): Promise<WorkItemRow> {
    return patch<WorkItemRow>(slug, `/work-items/${encodeURIComponent(id)}`, edits as unknown as Record<string, unknown>, signal);
  },

  archive(slug: string, id: string, signal?: AbortSignal): Promise<WorkItemRow> {
    return post<WorkItemRow>(slug, `/work-items/${encodeURIComponent(id)}/archive`, undefined, signal);
  },

  moveStage(slug: string, id: string, input: MoveStageInput, signal?: AbortSignal): Promise<WorkItemDetail> {
    return post<WorkItemDetail>(slug, `/work-items/${encodeURIComponent(id)}/move-stage`, input as unknown as Record<string, unknown>, signal);
  },

  close(slug: string, id: string, reason: string | null, signal?: AbortSignal): Promise<WorkItemDetail> {
    return post<WorkItemDetail>(slug, `/work-items/${encodeURIComponent(id)}/close`, { reason }, signal);
  },

  reopen(slug: string, id: string, signal?: AbortSignal): Promise<WorkItemDetail> {
    return post<WorkItemDetail>(slug, `/work-items/${encodeURIComponent(id)}/reopen`, undefined, signal);
  },
  // Brief 3 PR3 — take the CM off an Assigned case → re-queues to Ready-for-Assignment.
  unassignCm(slug: string, id: string, signal?: AbortSignal): Promise<WorkItemDetail> {
    return post<WorkItemDetail>(slug, `/work-items/${encodeURIComponent(id)}/unassign`, undefined, signal);
  },
  // Brief 3 PR3 — swap the CM on an already-Assigned case in place (wrong-CM fix).
  reassignCm(slug: string, id: string, rosterId: string, signal?: AbortSignal): Promise<WorkItemDetail> {
    return post<WorkItemDetail>(slug, `/work-items/${encodeURIComponent(id)}/reassign`, { assignee_roster_id: rosterId }, signal);
  },

  instantiateChecklist(slug: string, id: string, templateKey: string, signal?: AbortSignal): Promise<{ rows: WorkItemChecklistItemRow[] }> {
    return post<{ rows: WorkItemChecklistItemRow[] }>(slug, `/work-items/${encodeURIComponent(id)}/checklist`, { template_key: templateKey }, signal);
  },

  toggleChecklistItem(slug: string, id: string, itemId: string, done: boolean, signal?: AbortSignal): Promise<WorkItemChecklistItemRow> {
    return post<WorkItemChecklistItemRow>(slug, `/work-items/${encodeURIComponent(id)}/checklist/${encodeURIComponent(itemId)}/toggle`, { done }, signal);
  },

  setChecklistNote(slug: string, id: string, itemId: string, note: string | null, signal?: AbortSignal): Promise<WorkItemChecklistItemRow> {
    return patch<WorkItemChecklistItemRow>(slug, `/work-items/${encodeURIComponent(id)}/checklist/${encodeURIComponent(itemId)}`, { note }, signal);
  },

  // Template v2 (0124): set an allows_link item's URL (the document-folder links).
  setChecklistLink(slug: string, id: string, itemId: string, linkUrl: string | null, signal?: AbortSignal): Promise<WorkItemChecklistItemRow> {
    return patch<WorkItemChecklistItemRow>(slug, `/work-items/${encodeURIComponent(id)}/checklist/${encodeURIComponent(itemId)}`, { link_url: linkUrl }, signal);
  },
  // 0159: fill/clear a typed (date/text) item's value — filled = done, server-side.
  setChecklistValue(slug: string, id: string, itemId: string, value: string | null, signal?: AbortSignal): Promise<WorkItemChecklistItemRow> {
    return patch<WorkItemChecklistItemRow>(slug, `/work-items/${encodeURIComponent(id)}/checklist/${encodeURIComponent(itemId)}`, { value_text: value }, signal);
  },

  // Polish fix #1: correct a done item's completion date (done_at; YYYY-MM-DD).
  setChecklistDoneDate(slug: string, id: string, itemId: string, doneAt: string, signal?: AbortSignal): Promise<WorkItemChecklistItemRow> {
    return patch<WorkItemChecklistItemRow>(slug, `/work-items/${encodeURIComponent(id)}/checklist/${encodeURIComponent(itemId)}`, { done_at: doneAt }, signal);
  },

  addEvent(slug: string, id: string, input: { kind: 'comment'; body: string }, signal?: AbortSignal): Promise<WorkItemEventRow> {
    return post<WorkItemEventRow>(slug, `/work-items/${encodeURIComponent(id)}/events`, input as unknown as Record<string, unknown>, signal);
  },

  editEvent(slug: string, id: string, eventId: string, body: string, signal?: AbortSignal): Promise<WorkItemEventRow> {
    return patch<WorkItemEventRow>(slug, `/work-items/${encodeURIComponent(id)}/events/${encodeURIComponent(eventId)}`, { body }, signal);
  },
  /** Correct the day a card entered a stage (a stage_change event's entered_on; never in the future). */
  setEventEnteredOn(slug: string, id: string, eventId: string, enteredOn: string, signal?: AbortSignal): Promise<WorkItemEventRow> {
    return patch<WorkItemEventRow>(slug, `/work-items/${encodeURIComponent(id)}/events/${encodeURIComponent(eventId)}`, { entered_on: enteredOn }, signal);
  },

  deleteEvent(slug: string, id: string, eventId: string, signal?: AbortSignal): Promise<void> {
    return del(slug, `/work-items/${encodeURIComponent(id)}/events/${encodeURIComponent(eventId)}`, signal);
  },

  // Shared assignment (0108) — add/remove a collaborator on a card.
  addCollaborator(slug: string, id: string, userId: string, signal?: AbortSignal): Promise<{ collaborators: CollaboratorWire[]; request_id: string }> {
    return post<{ collaborators: CollaboratorWire[]; request_id: string }>(slug, `/work-items/${encodeURIComponent(id)}/collaborators`, { user_id: userId }, signal);
  },

  removeCollaborator(slug: string, id: string, userId: string, signal?: AbortSignal): Promise<void> {
    return del(slug, `/work-items/${encodeURIComponent(id)}/collaborators/${encodeURIComponent(userId)}`, signal);
  },

  // Workspace v3.x — hard-delete a one-off task (204; tasks only, server-guarded).
  deleteTask(slug: string, id: string, signal?: AbortSignal): Promise<void> {
    return del(slug, `/work-items/${encodeURIComponent(id)}`, signal);
  },

  // 0155 — hard-delete an UNCONVERTED intake (204; same DELETE dispatcher
  // route as deleteTask — the server routes by kind, server-guarded).
  deleteIntake(slug: string, id: string, signal?: AbortSignal): Promise<void> {
    return del(slug, `/work-items/${encodeURIComponent(id)}`, signal);
  },

  // 0160 — hard-delete a process-board card (204; same DELETE dispatcher
  // route — the server routes by kind, server-guarded).
  deleteProcessItem(slug: string, id: string, signal?: AbortSignal): Promise<void> {
    return del(slug, `/work-items/${encodeURIComponent(id)}`, signal);
  },

  // Workspace v3.x fast-follow — turn a one-off task recurring (attach in place; 201).
  attachRecurrence(slug: string, id: string, input: AttachRecurrenceInput, signal?: AbortSignal): Promise<CreateRecurringSeriesResult> {
    return post<CreateRecurringSeriesResult>(slug, `/work-items/${encodeURIComponent(id)}/recurrence`, input as unknown as Record<string, unknown>, signal);
  },

  // Workspace v3 Phase 2 — advance a recurring task in place (logs the completion,
  // rolls the same card forward to its next due date).
  completeRecurring(slug: string, id: string, signal?: AbortSignal): Promise<WorkItemDetail> {
    return post<WorkItemDetail>(slug, `/work-items/${encodeURIComponent(id)}/complete-recurring`, undefined, signal);
  },
};

// =====================================================================
// Recurrence (Workspace v3 Phase 2) — the recurring-series rule + its API.
// =====================================================================

export type RecurrenceCadence =
  | 'weekly'
  | 'every_two_weeks'
  | 'twice_monthly'
  | 'monthly'
  | 'last_business_day'
  | 'week_of'
  | 'specific_date';
export type RecurrenceBusinessDayRule = 'none' | 'next_business_day' | 'previous_business_day';

export interface RecurringSeriesRow {
  id: string;
  tenant_id: string;
  series_type: 'task' | 'process';
  owner_user_id: string;
  title: string;
  category: string | null;
  description: string | null;
  priority: WorkItemPriority;
  cadence: RecurrenceCadence;
  anchor_dow: number | null;
  anchor_dom: number | null;
  anchor_dom_2: number | null;
  anchor_date: string | null;
  week_of_dom: number | null;
  business_day_rule: RecurrenceBusinessDayRule;
  template_key: string | null;
  is_active: boolean;
  next_due: string | null;
  last_completed_at: string | null;
  last_completed_for: string | null;
  last_spawned_for: string | null;
  occurrence_count: number;
  created_by_user_id: string;
  updated_by_user_id: string;
  created_at: string;
  updated_at: string;
}

/** Turn an existing one-off task recurring — the schedule fields only (title/owner
 *  come from the task itself). */
export interface AttachRecurrenceInput {
  cadence: RecurrenceCadence;
  anchor_dow?: number;
  anchor_dom?: number;
  anchor_dom_2?: number;
  anchor_date?: string;
  week_of_dom?: number;
  business_day_rule?: RecurrenceBusinessDayRule;
}

export interface CreateRecurringSeriesInput {
  title: string;
  /** LP-2: the client the recurring task is about; rides its live instance. */
  client_id?: string | null;
  cadence: RecurrenceCadence;
  anchor_dow?: number;
  anchor_dom?: number;
  anchor_dom_2?: number;
  anchor_date?: string;
  week_of_dom?: number;
  business_day_rule?: RecurrenceBusinessDayRule;
  priority?: WorkItemPriority;
  category?: string | null;
  description?: string | null;
  owner_user_id?: string | null;
}

/** createRecurringSeries returns the rule + its first live work-item instance. */
export interface CreateRecurringSeriesResult {
  series: RecurringSeriesRow;
  instance: WorkItemRow;
}

export type UpdateRecurringSeriesInput = Partial<{
  title: string;
  category: string | null;
  description: string | null;
  priority: WorkItemPriority;
  // Editable cadence: a present `cadence` re-coerces the anchors + recomputes the
  // schedule (next_due + the live instance's due_date). Omit for a display-only edit.
  cadence: RecurrenceCadence;
  anchor_dow: number;
  anchor_dom: number;
  anchor_dom_2: number;
  anchor_date: string;
  week_of_dom: number;
  business_day_rule: RecurrenceBusinessDayRule;
}>;

export const recurringSeriesApi = {
  create(slug: string, input: CreateRecurringSeriesInput, signal?: AbortSignal): Promise<CreateRecurringSeriesResult> {
    return post<CreateRecurringSeriesResult>(slug, '/recurring-series', input as unknown as Record<string, unknown>, signal);
  },

  get(slug: string, id: string, signal?: AbortSignal): Promise<RecurringSeriesRow> {
    return get<RecurringSeriesRow>(slug, `/recurring-series/${encodeURIComponent(id)}`, signal);
  },

  update(slug: string, id: string, edits: UpdateRecurringSeriesInput, signal?: AbortSignal): Promise<RecurringSeriesRow> {
    return patch<RecurringSeriesRow>(slug, `/recurring-series/${encodeURIComponent(id)}`, edits as unknown as Record<string, unknown>, signal);
  },

  pause(slug: string, id: string, signal?: AbortSignal): Promise<RecurringSeriesRow> {
    return post<RecurringSeriesRow>(slug, `/recurring-series/${encodeURIComponent(id)}/pause`, undefined, signal);
  },
};

export interface PipelineStageWire {
  id: string;
  key: string;
  label: string;
  sort_order: number;
  phase_label: string | null;
  owner_role: string | null;
}

export interface CaseloadClientWire {
  id: string;
  display_name: string;
  // Demo/test client flag (0156) — note-authoring pickers filter to is_demo
  // clients for a demo-only author (board_observer). Server is the boundary.
  is_demo: boolean;
}

export interface CaseloadCmWire {
  roster_id: string;
  full_name: string;
  unit_id: string | null;
  unit_name: string | null;
  active_client_count: number;
  clients: CaseloadClientWire[];
}

export interface AssignCareManagerWire {
  roster_id: string;
  name: string;
  unit_id: string | null;
  /** Slice 3c: bio snippet for the picker preview. null = none on file. */
  bio: string | null;
}

export interface AssignUnitWire {
  id: string;
  name: string;
  kind: 'cm_geographic' | 'service_line';
  color: string | null; // Slice 3b — card/picker dot hex; NULL → palette slot in code
}

export interface WorkspaceMemberWire {
  user_id: string;
  name: string;
  role: string;
}

// Conversion metrics (v2 #2, read-only). The cohort partition + rates + the
// furthest-stage funnel + close-reason drop-off for a created_at period.
export interface ConversionFunnelStageWire {
  key: string;
  label: string;
  sort_order: number;
  furthest_count: number;
  // Honest outcome decomposition of the furthest-here bucket (the three sum to
  // furthest_count). Pre-convert stages carry only closed-unconverted (loss) or
  // in-progress (waiting); convert-line stages carry only converted (won). Lets
  // the funnel avoid painting waiting + won-and-processing clients as loss.
  furthest_converted: number;
  furthest_in_progress: number;
  furthest_closed_unconverted: number;
}
export interface ConversionCloseReasonWire {
  reason: string;
  count: number;
}
export interface ConversionMetricsResponse {
  cohort_total: number;
  converted: number;
  closed_unconverted: number;
  in_progress: number;
  conversion_rate: number; // converted / cohort_total (0..1)
  resolved_rate: number; // converted / (converted + closed_unconverted) (0..1)
  funnel: ConversionFunnelStageWire[];
  close_reasons: ConversionCloseReasonWire[];
  period: { from: string; to: string };
  request_id: string;
}

// Conversion drill-down (audit view) — the actual people behind a number.
export type ConversionDisposition = 'converted' | 'closed_unconverted' | 'in_progress';
export interface ConversionDrillRowWire {
  work_item_id: string;
  display_name: string | null;
  current_stage_label: string | null;
  disposition: ConversionDisposition;
  contact_name: string | null;
}
export interface ConversionDrillResponse {
  segment: string;
  rows: ConversionDrillRowWire[];
  count: number; // == rows.length; equals the clicked metric (trust contract)
  period: { from: string; to: string };
  request_id: string;
}

export const workspaceMetaApi = {
  // migration 0154: an optional processId scopes the board columns to one
  // process (absent = the tenant's intake process — today's default).
  stages(slug: string, processId?: string, signal?: AbortSignal): Promise<{ stages: PipelineStageWire[]; request_id: string }> {
    return get<{ stages: PipelineStageWire[]; request_id: string }>(slug, `/workspace/stages${api.buildQuery({ process: processId })}`, signal);
  },

  members(slug: string, signal?: AbortSignal): Promise<{ members: WorkspaceMemberWire[]; request_id: string }> {
    return get<{ members: WorkspaceMemberWire[]; request_id: string }>(slug, '/workspace/members', signal);
  },

  assignOptions(
    slug: string,
    signal?: AbortSignal,
  ): Promise<{ care_managers: AssignCareManagerWire[]; spice_therapists?: AssignCareManagerWire[]; units: AssignUnitWire[]; request_id: string }> {
    return get<{ care_managers: AssignCareManagerWire[]; spice_therapists?: AssignCareManagerWire[]; units: AssignUnitWire[]; request_id: string }>(
      slug,
      '/workspace/assign-options',
      signal,
    );
  },

  caseload(slug: string, signal?: AbortSignal): Promise<{ care_managers: CaseloadCmWire[]; request_id: string }> {
    return get<{ care_managers: CaseloadCmWire[]; request_id: string }>(slug, '/workspace/caseload', signal);
  },

  intakeOptions(slug: string, signal?: AbortSignal): Promise<IntakeOptions & { request_id: string }> {
    return get<IntakeOptions & { request_id: string }>(slug, '/workspace/intake-options', signal);
  },

  conversion(slug: string, params: { from: string; to: string }, signal?: AbortSignal): Promise<ConversionMetricsResponse> {
    return get<ConversionMetricsResponse>(slug, `/workspace/conversion${api.buildQuery({ from: params.from, to: params.to })}`, signal);
  },

  conversionDrill(
    slug: string,
    params: { from: string; to: string; segment: string },
    signal?: AbortSignal,
  ): Promise<ConversionDrillResponse> {
    return get<ConversionDrillResponse>(
      slug,
      `/workspace/conversion-drill${api.buildQuery({ from: params.from, to: params.to, segment: params.segment })}`,
      signal,
    );
  },
};

// =====================================================================
// Editable workspace processes (migration 0154, PR3) — multi-process boards
// =====================================================================

export type WorkProcessVisibility = 'all_staff' | 'restricted';

/** A process board (mirrors the server WorkProcessWire). */
export interface WorkProcessWire {
  id: string;
  key: string;
  label: string;
  sort_order: number;
  is_system: boolean; // the intake board — stage set locked, label rename only
  visibility: WorkProcessVisibility;
  is_active: boolean;
  allowed_roles: string[]; // [] for all_staff; the allow-list for restricted
  template_id: string | null; // the process's checklist template
}

/** A stage row incl. is_active (edit-mode read; the board read omits it). */
export interface WorkProcessStageWire {
  id: string;
  process_id: string;
  key: string;
  label: string;
  sort_order: number;
  phase_label: string | null;
  owner_role: string | null;
  is_active: boolean;
}

/** A checklist TEMPLATE item (the editor's unit — distinct from the per-card
 *  work_item_checklist_items instance). */
export interface WorkTemplateItemWire {
  id: string;
  template_id: string;
  sort_order: number;
  label: string;
  owner_role: string | null;
  maps_to_stage_key: string | null;
  is_gate: boolean;
  is_optional: boolean;
  allows_note: boolean;
  allows_link: boolean;
  parent_id: string | null;
  link_key: string | null;
  item_type: string; // 0159 — 'checkbox' | 'date' | 'text'
}

/** Server-computed dry-run impact facts (the founder's warning numbers). */
export interface DryRunImpact {
  open_cards_affected: number;
  items_reset: number;
  gates_added_blocking: number;
  progress_counts_changed: number;
}

export interface CreateProcessInput {
  label: string;
  visibility?: WorkProcessVisibility;
  allowed_roles?: string[];
}
export interface UpdateProcessInput {
  label?: string;
  sort_order?: number;
  visibility?: WorkProcessVisibility;
  allowed_roles?: string[];
}
export interface CreateStageInput {
  label: string;
  phase_label?: string | null;
  owner_role?: string | null;
}
export interface UpdateStageInput {
  label?: string;
  sort_order?: number;
  phase_label?: string | null;
  owner_role?: string | null;
}
export interface TemplateItemInput {
  label?: string;
  maps_to_stage_key?: string | null;
  is_gate?: boolean;
  is_optional?: boolean;
  allows_note?: boolean;
  allows_link?: boolean;
  parent_id?: string | null;
  sort_order?: number;
  item_type?: 'checkbox' | 'date' | 'text'; // 0159 — the step's input shape
}

/** Raw dry-run/commit envelope off the wire (entity under a per-kind key). */
interface RawOutcome {
  dry_run: boolean;
  impact: DryRunImpact;
  process?: WorkProcessWire | null;
  stage?: WorkProcessStageWire | null;
  item?: WorkTemplateItemWire | null;
}

const dq = (dryRun?: boolean): string => (dryRun ? '?dry_run=1' : '');

export const workspaceProcessesApi = {
  list(slug: string, signal?: AbortSignal): Promise<{ processes: WorkProcessWire[]; request_id: string }> {
    return get<{ processes: WorkProcessWire[]; request_id: string }>(slug, '/workspace/processes', signal);
  },
  // Edit-mode stage list (active + inactive) for a process.
  stages(slug: string, processId: string, signal?: AbortSignal): Promise<{ stages: WorkProcessStageWire[]; request_id: string }> {
    return get<{ stages: WorkProcessStageWire[]; request_id: string }>(slug, `/workspace/processes/${encodeURIComponent(processId)}/stages`, signal);
  },
  templateItems(slug: string, templateId: string, signal?: AbortSignal): Promise<{ items: WorkTemplateItemWire[]; request_id: string }> {
    return get<{ items: WorkTemplateItemWire[]; request_id: string }>(slug, `/workspace/templates/${encodeURIComponent(templateId)}/items`, signal);
  },

  async createProcess(slug: string, input: CreateProcessInput, dryRun?: boolean): Promise<RawOutcome> {
    return post<RawOutcome>(slug, `/workspace/processes${dq(dryRun)}`, input as unknown as Record<string, unknown>);
  },
  async updateProcess(slug: string, id: string, input: UpdateProcessInput, dryRun?: boolean): Promise<RawOutcome> {
    return patch<RawOutcome>(slug, `/workspace/processes/${encodeURIComponent(id)}${dq(dryRun)}`, input as unknown as Record<string, unknown>);
  },
  async archiveProcess(slug: string, id: string, dryRun?: boolean): Promise<RawOutcome> {
    return post<RawOutcome>(slug, `/workspace/processes/${encodeURIComponent(id)}/archive${dq(dryRun)}`, undefined);
  },
  async restoreProcess(slug: string, id: string, dryRun?: boolean): Promise<RawOutcome> {
    return post<RawOutcome>(slug, `/workspace/processes/${encodeURIComponent(id)}/restore${dq(dryRun)}`, undefined);
  },
  async createStage(slug: string, processId: string, input: CreateStageInput, dryRun?: boolean): Promise<RawOutcome> {
    return post<RawOutcome>(slug, `/workspace/processes/${encodeURIComponent(processId)}/stages${dq(dryRun)}`, input as unknown as Record<string, unknown>);
  },
  async updateStage(slug: string, id: string, input: UpdateStageInput, dryRun?: boolean): Promise<RawOutcome> {
    return patch<RawOutcome>(slug, `/workspace/stages/${encodeURIComponent(id)}${dq(dryRun)}`, input as unknown as Record<string, unknown>);
  },
  async deactivateStage(slug: string, id: string, dryRun?: boolean): Promise<RawOutcome> {
    return post<RawOutcome>(slug, `/workspace/stages/${encodeURIComponent(id)}/deactivate${dq(dryRun)}`, undefined);
  },
  async reactivateStage(slug: string, id: string, dryRun?: boolean): Promise<RawOutcome> {
    return post<RawOutcome>(slug, `/workspace/stages/${encodeURIComponent(id)}/reactivate${dq(dryRun)}`, undefined);
  },
  async addTemplateItem(slug: string, templateId: string, input: TemplateItemInput, dryRun?: boolean): Promise<RawOutcome> {
    return post<RawOutcome>(slug, `/workspace/templates/${encodeURIComponent(templateId)}/items${dq(dryRun)}`, input as unknown as Record<string, unknown>);
  },
  async updateTemplateItem(slug: string, id: string, input: TemplateItemInput, dryRun?: boolean): Promise<RawOutcome> {
    return patch<RawOutcome>(slug, `/workspace/templates/items/${encodeURIComponent(id)}${dq(dryRun)}`, input as unknown as Record<string, unknown>);
  },
  async deleteTemplateItem(slug: string, id: string, dryRun?: boolean): Promise<RawOutcome> {
    // The delete path returns { dry_run, impact, item } (200, not 204); dry-run
    // rides the query string and the server rolls back.
    return delJson<RawOutcome>(slug, `/workspace/templates/items/${encodeURIComponent(id)}${dq(dryRun)}`);
  },
};

// =====================================================================
// Billing slice A4 — invoices (compile / state machine / issue / preview)
// =====================================================================

export interface InvoiceWire {
  id: string;
  client_id: string;
  billing_contact_id: string | null;
  doc_number: string | null;
  status: string;
  period_start: string;
  period_end: string;
  terms: string | null;
  subtotal: string;
  total: string;
  approved_at: string | null;
  issued_at: string | null;
  created_at: string;
}

export interface InvoiceLineWire {
  id: string;
  line_kind: string;
  billable_event_id: string | null;
  description: string;
  quantity: string;
  unit_rate: string;
  amount: string;
  sort_order: number;
  /** True when the server found the line's band to be the after-hours
   *  routing label; the band itself is not served. The server always
   *  sends it; a line without it reads as not after hours. */
  after_hours?: boolean;
  /** Whose care the line was, on a household invoice (0215). Absent or
   *  null on a single person's invoice. */
  service_label?: string | null;
}

export interface InvoiceBillToWire {
  id: string;
  contact_name: string;
  relationship: string | null;
  email: string | null;
  address: string | null;
}

export interface InvoiceDetailWire {
  invoice: InvoiceWire;
  client: { id: string; display_name: string; caretree_external_id: string };
  bill_to: InvoiceBillToWire | null;
  /** 'stamped' = the frozen post-issue contact; 'live' = a pre-issue
   *  preview's on-the-fly resolve of the client's CURRENT primary
   *  (founder feedback 2026-07-13 — billing_contact_id stamps only at
   *  issue, so a draft/approved preview used to show "No primary bill-to
   *  set" even with a valid primary on file); null = genuinely none. */
  bill_to_resolution: 'stamped' | 'live' | null;
  lines: InvoiceLineWire[];
  /** Where `lines` came from. 'frozen' = the invoice is void and the lines
   *  are the issue-time snapshot document (voidInvoice deletes the live
   *  rows); 'live' = invoice_line_items, as for every other status. */
  lines_source: 'live' | 'frozen';
  /** The client's case notes dated in the period, and how many are not
   *  Ready; null for a void invoice or a seat that does not read every
   *  client's notes (the server decides). */
  note_count: { total: number; not_ready: number } | null;
  request_id: string;
}

/** List rows carry the client's display name (the list leads with the
 *  client, not the doc # — founder feedback 2026-07-10). */
export interface InvoiceListRowWire extends InvoiceWire {
  client_display_name: string;
  /** A demo client: the row is labelled "Demo client"; every act is the
   *  same as for any other row. */
  client_is_demo: boolean;
  /** The client's case notes in this invoice's period that are not Ready;
   *  null for a void invoice or a seat that does not read every client
   *  (the server decides). */
  not_ready_count: number | null;
  /** The stage-7 instrument (founder ruling 2026-08-27). What OLIVE DID:
   *  'never_pushed' | 'pending' | 'pushed' | 'failed'. NOT what QuickBooks
   *  holds now — an invoice deleted over there still reads 'pushed'. */
  qbo_push_status: string;
  /** Set once ANY attempt pushed, and sticky across later failures. */
  qbo_invoice_id: string | null;
  /** The latest failure's fact-shaped class token, e.g. 'band_unmapped'. */
  qbo_last_error: string | null;

  // ---- what Olive last SAW of the QuickBooks copy (0171) -------------
  // The fields above say what Olive DID. These say what Olive last
  // OBSERVED, and they exist because Olive's push is create-only: an
  // invoice voided here leaves a live copy over there collecting payment
  // until a person closes it. Every one of them is a stored fact with an
  // as-of time, never a live read, so the list renders without Intuit.
  /** 'open' | 'open_with_payment' | 'already_void' | 'missing_in_qbo'
   *  | 'check_unavailable'. NULL means nobody has looked, which is a
   *  different sentence from every value it can hold. */
  qbo_copy_state: string | null;
  /** The QuickBooks document the observation was ABOUT. When it differs
   *  from qbo_invoice_id above, the fact is about a DIFFERENT copy and
   *  the screen discards it rather than showing it. */
  qbo_copy_of_id: string | null;
  /** ISO. When QuickBooks was ASKED, not when the row was written. */
  qbo_copy_checked_at: string | null;
  /** ISO. When the copy was first seen open after the Olive void: the age
   *  of the PROBLEM, which a fresh check must not reset. */
  qbo_copy_open_since: string | null;
  /** A human confirmed a vanished copy was removed on purpose. */
  qbo_copy_acknowledged: boolean;
  /** The no-answer copy search (0214): see VoidCopyRow.qbo_copy_lookup.
   *  Optional only so an older server's rows still parse; absent reads as
   *  null. */
  qbo_copy_lookup?: 'pending' | 'unavailable' | 'not_found' | null;
  // unavailable_reason is deliberately NOT here. The list carries the
  // stored summary; the detail page re-runs the check and says precisely
  // what went wrong, next to the QuickBooks connection card.
}

export interface InvoiceListWire {
  rows: InvoiceListRowWire[];
  /** The server's honest count of the FILTERED set (month, client, status,
   *  needs, scope), which is what paging reads. `rows` is one page of it. */
  total: number;
  /** The month-end counts (contract item 5; third count 2026-08-31),
   *  computed SERVER-SIDE over the whole filtered month: to_send_count is
   *  issued-and-never-landed, to_fix_count is
   *  voided-here-with-an-unresolved-QuickBooks-copy, unconfirmed_count is
   *  issued-and-landed-but-the-total-was-never-compared
   *  (total_unverifiable; total_mismatch joins no count, per the
   *  2026-08-28 ruling). They follow the month filter ONLY, never the
   *  client, status, needs, SCOPE, limit or offset — the scoreboard is
   *  the month's, whatever slice of it is on screen. REQUIRED on purpose:
   *  optional would let a `server ?? derivedFromRows` implementation
   *  typecheck, and a fallback that tallies the page is the exact defect
   *  these fields replace. */
  to_send_count: number;
  to_fix_count: number;
  unconfirmed_count: number;
  /** The part of to_fix_count that is the no-answer copy search's second
   *  arm (0214): voided invoices whose unanswered send may have left a
   *  copy Olive has not found yet. Optional only so an older server's
   *  answer still parses; absent renders no line. */
  copy_lookup_count?: number;
  /** The sync error alert (PR 4b): invoices whose latest QuickBooks push
   *  failed, none of whose attempts ever landed, and that are not void.
   *  Tenant-wide, never narrowed by the month or any filter or page. The
   *  count is uncapped; push_failed is the newest 50. Optional only so an
   *  older server's answer renders no alert; nothing is derived from
   *  `rows` in its place. */
  push_failed_count?: number;
  push_failed?: PushFailedRowWire[];
  request_id: string;
}

/** One invoice in the sync error alert. */
export interface PushFailedRowWire {
  id: string;
  doc_number: string | null;
  client_id: string;
  client_display_name: string;
  qbo_last_error: string | null;
}

export interface CompileInvoiceBody {
  client_id: string;
  period_start: string;
  period_end: string;
}

// NOTE (nav rework 2026-07-13): the FE dashboardApi client + Revenue*Wire
// types (UX-4/UX-9 revenue indicator) were deleted with the retired X.1
// Dashboard page. The backend GET /v1/dashboard/revenue handler was
// removed in the 2026-07-18 dead-code cleanup.

// =====================================================================
// Reports Phase A — referral-partner analytics wire types + client.
// Mirrors api/_shared/repos/referralReports.ts.
// =====================================================================

export type ReferralSourceKind = 'org' | 'individual' | 'text' | 'category' | 'none';

export interface ReferralSourceRef {
  kind: ReferralSourceKind;
  key: string;
  label: string;
  /** Directory entry id for a link (org/individual); null otherwise. */
  directory_id: string | null;
}

export interface ReferralWeekWire {
  week_start: string;
  week_end: string;
  is_partial: boolean;
  new_referrals: number;
  conversions: number;
}

export interface ReferralPartnerRowWire {
  source: ReferralSourceRef;
  this_week: number;
  /** Mean over the trailing COMPLETE weeks only (the partial current week is excluded). */
  recent_avg: number;
  window_total: number;
  conversions: number;
  conversion_rate: number;
  /** Per-week new referrals for THIS source, oldest-first, index-aligned to
   *  `buckets` (additive; feeds the partner-engagement per-partner referral
   *  sparkline + pie lift). Optional so an older cached response degrades. */
  weekly_new_referrals?: number[];
  /** The partner's referral CATEGORY: the DOMINANT `referrer_type` (the intake
   *  tracker's category) across this partner's intakes (founder ruling
   *  2026-07-15 — referrer_type ONLY, never the Directory taxonomy). null when
   *  the partner has no categorized intakes and no default is configured
   *  (renders as a dash). Additive; optional so an older cached response
   *  degrades. */
  category_label?: string | null;
  /** Distinct referrer_type values BEYOND the dominant (the "(+N other)"
   *  indicator; 0 when uniform/uncategorized). Additive. */
  category_other_count?: number;
  /** The distinct referrer_type values on this partner's intakes — powers the
   *  channel filter (narrow the table to partners in the selected channel).
   *  Additive; optional so an older cached response degrades. */
  referrer_type_values?: string[];
}

/** One referral CHANNEL (a configured referrer_type value) rollup over the
 *  window — the filter chip's numbers + channel-performance analytic. */
export interface ReferralChannelWire {
  value: string;
  label: string;
  referrals: number;
  conversions: number;
  conversion_rate: number;
}

export interface ReferralWeeklyReportWire {
  weeks: number;
  /** Same value as `weeks` — an explicit "resolved" field (additive) so a
   *  `weeks: 'all'` request doesn't require inferring whether `weeks` is the
   *  raw request or the post-resolution bucket count. Window presets. */
  weeks_resolved: number;
  /** True when the window was resolved via the earliest-intake all-time
   *  anchor (a `weeks: 'all'` request) rather than a fixed integer.
   *  Additive; window presets. */
  is_all_time: boolean;
  buckets: ReferralWeekWire[];
  partners: ReferralPartnerRowWire[];
  /** One rollup per configured referrer_type channel, in config order.
   *  Additive; optional so an older cached response degrades to no chips. */
  channels?: ReferralChannelWire[];
  converted_stage_key: string;
  entered_intake_stage_key: string;
  generated_at: string;
}

/** The referral drill response — the intakes behind a partner row / channel
 *  chip (reuses the conversion drill's row shape so the drawer path + panel
 *  are shared). `count` == rows.length == the clicked number (trust contract). */
export interface ReferralDrillResponse {
  segment: string;
  rows: ConversionDrillRowWire[];
  count: number;
  request_id: string;
}

// =====================================================================
// Weekly intake funnel wire types. Mirrors api/_shared/repos/intakeFunnel.ts.
// =====================================================================

export type FunnelDimension = 'unit' | 'county' | 'referrer';

/** The four counts every funnel row carries; the last three sum to the first. */
export interface FunnelCountsWire {
  inquiries: number;
  converted: number;
  waiting: number;
  turned_away: number;
}

/** One week's totals (Monday to Sunday in the practice's zone). Newest first. */
export interface FunnelWeekWire extends FunnelCountsWire {
  week_start: string;
  week_end: string;
  is_partial: boolean;
}

/** One (week, value) cell of a breakdown; the "Not recorded" row sinks last. */
export interface FunnelBreakdownRowWire extends FunnelCountsWire {
  week_start: string;
  key: string;
  label: string;
}

export interface FunnelBreakdownWire {
  dimension: FunnelDimension;
  label: string;
  rows: FunnelBreakdownRowWire[];
}

export interface IntakeFunnelReportWire {
  weeks: number;
  time_zone: string;
  by_week: FunnelWeekWire[];
  breakdowns: FunnelBreakdownWire[];
  generated_at: string;
}

export const reportsApi = {
  funnelWeekly(slug: string, params?: { weeks?: number }, signal?: AbortSignal): Promise<IntakeFunnelReportWire> {
    const qs = new URLSearchParams();
    if (params?.weeks != null) qs.set('weeks', String(params.weeks));
    const suffix = qs.toString() ? `?${qs.toString()}` : '';
    return get<IntakeFunnelReportWire>(slug, `/reports/funnel/weekly${suffix}`, signal);
  },

  referralsWeekly(
    slug: string,
    params?: { weeks?: number | 'all'; referrerType?: string | null },
    signal?: AbortSignal,
  ): Promise<ReferralWeeklyReportWire> {
    const qs = new URLSearchParams();
    if (params?.weeks != null) qs.set('weeks', String(params.weeks));
    // Channel scope: per-partner numbers reflect only this referrer_type.
    if (params?.referrerType) qs.set('referrer_type', params.referrerType);
    const suffix = qs.toString() ? `?${qs.toString()}` : '';
    return get<ReferralWeeklyReportWire>(slug, `/reports/referrals/weekly${suffix}`, signal);
  },

  referralsDrill(
    slug: string,
    params: { weeks?: number | 'all'; segment: string },
    signal?: AbortSignal,
  ): Promise<ReferralDrillResponse> {
    const qs = new URLSearchParams();
    if (params.weeks != null) qs.set('weeks', String(params.weeks));
    qs.set('segment', params.segment);
    return get<ReferralDrillResponse>(slug, `/reports/referrals/drill?${qs.toString()}`, signal);
  },
};

export const invoicesApi = {
  list(
    slug: string,
    params?: {
      status?: string;
      client_id?: string;
      /** 'YYYY-MM'. The server expands it into inclusive month bounds and
       *  matches on period OVERLAP, so a widened invoice shows under both
       *  months it touches. */
      month?: string;
      /** The pile filter behind a month-end count's click-through. Narrows
       *  the rows and `total` server-side; the counts stay the month's.
       *  An explicit pile overrides `scope` on the server, so the
       *  QuickBooks pile keeps its void rows under the live default. */
      needs?: 'send' | 'qbo' | 'unconfirmed';
      /** The lifecycle scope (D1(b), 2026-08-31). The page sends it
       *  EXPLICITLY, 'live' included: the server defaults an absent scope
       *  to 'live' anyway, but a view that rides the server's default is
       *  a view whose meaning can change under it. */
      scope?: 'live' | 'void' | 'all';
      limit?: number;
      offset?: number;
    },
    signal?: AbortSignal,
  ): Promise<InvoiceListWire> {
    const qs = new URLSearchParams();
    if (params?.status) qs.set('status', params.status);
    if (params?.client_id) qs.set('client_id', params.client_id);
    if (params?.month) qs.set('month', params.month);
    if (params?.needs) qs.set('needs', params.needs);
    if (params?.scope) qs.set('scope', params.scope);
    // Sent EXPLICITLY whenever given, zero included: a page that omits
    // limit= rides whatever default the server happens to hold, and the
    // control above the table starts describing a page size it never asked
    // for.
    if (params?.limit != null) qs.set('limit', String(params.limit));
    if (params?.offset != null) qs.set('offset', String(params.offset));
    const suffix = qs.toString() ? `?${qs.toString()}` : '';
    return get<InvoiceListWire>(slug, `/invoices${suffix}`, signal);
  },

  get(slug: string, id: string, signal?: AbortSignal): Promise<InvoiceDetailWire> {
    return get<InvoiceDetailWire>(slug, `/invoices/${encodeURIComponent(id)}`, signal);
  },

  compile(
    slug: string,
    body: CompileInvoiceBody,
    signal?: AbortSignal,
  ): Promise<{ invoice: InvoiceWire; request_id: string }> {
    return post<{ invoice: InvoiceWire; request_id: string }>(
      slug,
      '/invoices/compile',
      body as unknown as Record<string, unknown>,
      signal,
    );
  },

  approve(slug: string, id: string, signal?: AbortSignal): Promise<{ invoice: InvoiceWire; request_id: string }> {
    return post<{ invoice: InvoiceWire; request_id: string }>(slug, `/invoices/${encodeURIComponent(id)}/approve`, undefined, signal);
  },

  reopen(slug: string, id: string, signal?: AbortSignal): Promise<{ invoice: InvoiceWire; request_id: string }> {
    return post<{ invoice: InvoiceWire; request_id: string }>(slug, `/invoices/${encodeURIComponent(id)}/reopen`, undefined, signal);
  },

  // Draft-only: re-derive lines from current billable_events (the one
  // shared derivation) so upstream Pre-billing edits show before approve.
  /** Add unbilled charges: how many charges the run added and removed. */
  recompile(
    slug: string,
    id: string,
    signal?: AbortSignal,
  ): Promise<{ invoice: InvoiceWire; added_count: number; removed_count: number; request_id: string }> {
    return post<{ invoice: InvoiceWire; added_count: number; removed_count: number; request_id: string }>(
      slug,
      `/invoices/${encodeURIComponent(id)}/recompile`,
      undefined,
      signal,
    );
  },

  issue(slug: string, id: string, signal?: AbortSignal): Promise<{ invoice: InvoiceWire; doc_number: string; request_id: string }> {
    return post<{ invoice: InvoiceWire; doc_number: string; request_id: string }>(slug, `/invoices/${encodeURIComponent(id)}/issue`, undefined, signal);
  },

  discard(slug: string, id: string, signal?: AbortSignal): Promise<void> {
    return del(slug, `/invoices/${encodeURIComponent(id)}`, signal);
  },

  // issued -> void (BILL-4 / migration 0116). Frees the invoice's events
  // (billed_on_invoice_id -> NULL) so they return to the client's
  // Pre-billing tab as Ready lines; the reason is required server-side
  // (validateVoidInvoice: non-empty, <=500 chars).
  void(
    slug: string,
    id: string,
    reason: string,
    signal?: AbortSignal,
  ): Promise<{ invoice: InvoiceWire; request_id: string }> {
    return post<{ invoice: InvoiceWire; request_id: string }>(
      slug,
      `/invoices/${encodeURIComponent(id)}/void`,
      { reason },
      signal,
    );
  },
};

// =====================================================================
// UNBILLED WORK BY CLIENT (contract item 3). Mirrors
// api/_shared/repos/unbilledWork.ts.
//
// The list above is FROM invoices, so work that never reached an invoice
// has no row and raises no count. This is the read that looks for the
// money that is NOT there.
//
// Deliberately NOT under /v1/invoices. A sibling route under that prefix
// is served the invoice-list body by both fetch mocks in
// tests/ui/Invoices.test.tsx, which match it by SUBSTRING, and every
// absence test would then pass without exercising anything.
// =====================================================================

/** Each value names the ACTION that clears it, which is what makes five
 *  values proportionate: five distinct remedies, not five ways of saying
 *  "unbilled". */
export type UnbilledReason =
  | 'reconcile_first'
  | 'invoice_not_sent'
  | 'invoice_already_issued'
  | 'not_invoiced'
  | 'no_ready_notes'
  | 'rate_card';

/** One CLIENT. The unit of this report is the client, not the event. */
export interface UnbilledWorkRowWire {
  client_id: string;
  client_display_name: string;
  /** Archived clients are INCLUDED (founder ruling): if the archiving was
   *  the mistake, excluding them hides the case this exists to catch. The
   *  flag drives the sentence saying the client must be un-archived
   *  before it can be compiled, because the compile form's picker is
   *  built from active clients only and cannot reach it. */
  is_archived: boolean;
  /** A demo client: the table labels the row "Demo client". A label
   *  only: the row is compiled like any other. */
  is_demo: boolean;
  /** numeric(12,2) as TEXT, never a JS number: money columns never
   *  coerce to float. null = there is no event to price, which is the
   *  rate_card-only row, and must render differently from '0.00'. */
  amount: string | null;
  /** Same rule as `amount`. */
  hours: string | null;
  /** A SET, not one value: the event reason first, then
   *  no_ready_notes, then rate_card. */
  reasons: UnbilledReason[];
  /** On a no_ready_notes row, the notes waiting to be Ready; 0 on every
   *  other row. */
  not_ready_count: number;
  /** True when a draft or approved invoice's period contains the whole
   *  month. On an invoice_not_sent row, false means the unsent invoice
   *  covers only part of the month. The server always sends it; a row
   *  without it reads as covering the month. */
  unsent_covers_month?: boolean;
  /** A partner's payer (0215): whose invoice this client's work goes on.
   *  Null or absent for a payer or a household of one. */
  bills_with?: { client_id: string; display_name: string } | null;
}

export interface UnbilledWorkWire {
  rows: UnbilledWorkRowWire[];
  /** '0.00' when there are no rows. The number an unbilled report exists
   *  to produce, computed SERVER-SIDE over the month rather than tallied
   *  from whatever rows arrived. */
  total_amount: string;
  total_hours: string;
  /** 0 = no period note. Counted PER NOTE, so a native_from that falls
   *  mid-month still raises it. */
  pre_native_ready_count: number;
  native_from: string | null;
  request_id: string;
}

export const unbilledApi = {
  /** Month only. The report follows the month picker and ignores the
   *  client filter, the pile filter and the page offset, for the reason
   *  already ruled for the two counts beside it: a number scoped to one
   *  client turns "zero for this client" into a false "the month can
   *  close". */
  list(slug: string, month: string, signal?: AbortSignal): Promise<UnbilledWorkWire> {
    const qs = new URLSearchParams();
    qs.set('month', month);
    return get<UnbilledWorkWire>(slug, `/unbilled?${qs.toString()}`, signal);
  },
};

// =====================================================================
// Reports Phase B — weekly operating metrics wire types + client (0151).
// Mirrors api/_shared/repos/operatingMetrics.ts. A DISTINCT api object
// (operatingMetricsApi) from Phase A's reportsApi so the two Reports
// slices don't collide on merge; reconcile into one namespace later.
// =====================================================================

export type OperatingMetricStatus = 'preliminary' | 'final' | 'restated';

// --- sections (payload v2, migration 0153) — the rich Monday-dashboard bag.
// Money stays as numeric-STRINGS end-to-end (the no-float posture). Every
// section is optional: a section renders ONLY when present (graceful
// degradation). quiet_clients + collections carry client DISPLAY NAMES
// (opaque strings, aggregate billing facts — the 0153 PHI amendment).
export interface OperatingMonthlyPoint {
  month: string; // YYYY-MM
  actual: string | null;
  plan: string | null;
  prior_year: string | null;
}
export type OperatingArBucketKey = 'current' | '1-30' | '31-60' | '61-90' | '91+';
export interface OperatingArBucket {
  bucket: OperatingArBucketKey;
  collectible: string | null;
  reserved: string | null;
}
export interface OperatingArAging {
  buckets: OperatingArBucket[];
  note?: string | null;
}
export interface OperatingCollectionsRow {
  client_name: string;
  aged_amount: string | null;
  total_on_book?: string | null;
}
export interface OperatingCollections {
  rows: OperatingCollectionsRow[];
  note?: string | null;
}
export interface OperatingExpenses {
  spend_this_week: string | null;
  client_billable: string | null;
  to_invoice_with_markup: string | null;
  month_to_date: string | null;
  note?: string | null;
}
export type OperatingClinicianRole = 'cm' | 'spice';
export interface OperatingClinicianHours {
  name: string;
  role: OperatingClinicianRole;
  hours: string | null;
}
export interface OperatingQuietClientRow {
  client_name: string;
  avg_week_dollars: string | null;
  last_billed: string | null;
}
/** DEPRECATED (B.3 founder pass): still ACCEPTED by the validator for old
 * stored rows + a transitional payload, but NO LONGER RENDERED — replaced by
 * `top_clients`. */
export interface OperatingQuietClients {
  rows: OperatingQuietClientRow[];
  note?: string | null;
}
/** One top-revenue client for the period (B.3 founder pass; replaces the
 * quiet-clients card). Client DISPLAY NAMES are OPAQUE STRINGS + an aggregate
 * billing fact (the 0153 PHI amendment — no client_id UUIDs, no FKs). */
export interface OperatingTopClient {
  client_name: string;
  services: OperatingClinicianRole[]; // 'cm' | 'spice', non-empty
  total_revenue: string | null;
  avg_weekly_revenue: string | null;
  last_seen: string | null; // YYYY-MM-DD
}
/** One referral-partner engagement row for a week (sections.partners —
 * build-specs/partner-engagement-analytics.md). `touches` is the core metric
 * (interactions logged that week); `pie` is an optional pie-delivered date.
 * Names are OPAQUE STRINGS (partner names, never client names). */
export interface OperatingPartnerTouch {
  name: string;
  tier?: string | null;
  touches: number;
  pie?: string | null;
}
export interface OperatingSections {
  monthly?: OperatingMonthlyPoint[];
  ar_aging?: OperatingArAging;
  collections?: OperatingCollections;
  expenses?: OperatingExpenses;
  clinician_hours?: OperatingClinicianHours[];
  top_clients?: OperatingTopClient[];
  /** DEPRECATED (B.3): accepted, no longer rendered (replaced by top_clients). */
  quiet_clients?: OperatingQuietClients;
  partners?: OperatingPartnerTouch[];
  /** DEPRECATED (B.3): accepted, no longer rendered (editorial box removed). */
  narrative?: string;
  hours_note?: string;
  cash_ar_note?: string;
  sources_note?: string;
}

/** One active-roster contracted-target row (B.3 founder pass) carried
 * additively on the operating GET response. Sourced from Kith substrate
 * (staff_roster.target_weekly_hours), NOT the weekly payload; matched to
 * clinician_hours names FE-side by normalized name (best-effort, display-only).
 * `target_weekly_hours` is numeric-as-text (no-float posture). */
export interface OperatingRosterTarget {
  name: string;
  target_weekly_hours: string;
}

export interface WeeklyOperatingMetricWire {
  week_ending: string; // YYYY-MM-DD Sunday
  total_revenue: string | null;
  cm_revenue: string | null;
  spice_revenue: string | null;
  total_hours: string | null;
  clients_touched: number | null;
  ar_total: string | null;
  ar_collectible_60plus: string | null;
  ar_reserved: string | null;
  cash: string | null;
  plan_total: string | null;
  plan_cm: string | null;
  plan_spice: string | null;
  crm_new_leads: number | null;
  crm_active_pipeline: number | null;
  crm_partner_touches: number | null;
  status: OperatingMetricStatus;
  source_note: string | null;
  sections: OperatingSections | null; // 0153 rich-dashboard payload (or null)
  updated_at: string;
  /** 'billing' = summed live from Olive's billing ledger (a week on or after
   *  billing.native_from); 'upload' = the Monday upload. */
  origin: 'upload' | 'billing';
}

export interface OperatingMetricsReportWire {
  weeks: number;
  rows: WeeklyOperatingMetricWire[];
  /** Active-roster contracted targets (B.3) — absent on older backends. */
  roster_targets?: OperatingRosterTarget[];
  generated_at: string;
}

export interface OperatingMetricIngestResultWire {
  week_ending: string;
  status: OperatingMetricStatus;
  outcome: 'created' | 'updated' | 'restated' | 'unchanged';
}

export const operatingMetricsApi = {
  weekly(
    slug: string,
    params?: { weeks?: number },
    signal?: AbortSignal,
  ): Promise<OperatingMetricsReportWire> {
    const qs = new URLSearchParams();
    if (params?.weeks != null) qs.set('weeks', String(params.weeks));
    const suffix = qs.toString() ? `?${qs.toString()}` : '';
    return get<OperatingMetricsReportWire>(slug, `/reports/operating/weekly${suffix}`, signal);
  },

  // The upload door: POST the Monday routine's payload (one week object, an
  // array of weeks, or { weeks: [...] }). Gated ingest_operating_metrics.
  ingest(
    slug: string,
    body: Record<string, unknown>,
    signal?: AbortSignal,
  ): Promise<{ results: OperatingMetricIngestResultWire[] }> {
    return post<{ results: OperatingMetricIngestResultWire[] }>(slug, `/reports/operating/weekly`, body, signal);
  },
};
